<template>
  <div class="od-root">

    <!-- detail mode: an event_id in the query routes us into the detail
         view. keeps a single route entry and matches the support-ticket
         shape (/help_center?tab=support&ticket_id=N). -->
    <div v-if="event_id" class="od-detail">

      <div class="od-rail">
        <button class="od-rail-back od-no-print" type="button" @click="goBackToList" :aria-label="'Back to events'" title="Back to events">
          <i class="fa fa-arrow-left"></i>
        </button>
        <div class="od-rail-text">
          <div class="od-rail-eyebrow">Ops · Event Response Manager</div>
          <h3 class="od-rail-title" v-if="model">
            Event #{{ model.id }}
            <span v-if="contextTenantName" class="od-rail-tenant-chip">{{ contextTenantName }}</span>
          </h3>
          <h3 class="od-rail-title od-rail-title--muted" v-else>
            Event
            <span v-if="contextTenantName" class="od-rail-tenant-chip">{{ contextTenantName }}</span>
          </h3>
        </div>
        <div class="od-rail-actions od-no-print">
          <b-button v-if="!isEditing && model" variant="outline-secondary" size="sm" @click="enterEditMode()">
            <i class="fa fa-pencil"></i>&nbsp;Edit
          </b-button>
          <!-- FI-1932: print a clean single-column copy of the event. the
               print stylesheet hides the app chrome and every screen-only
               control (this rail included). -->
          <b-button v-if="!isEditing && model" variant="link" size="sm" @click="printDetail" title="Print this event">
            <i class="fa fa-print"></i>&nbsp;Print
          </b-button>
          <template v-if="isEditing">
            <b-button variant="primary" size="sm" :disabled="saving" @click="saveEdits">
              <i class="fa fa-check"></i>&nbsp;Save
            </b-button>
            <b-button variant="link" size="sm" :disabled="saving" @click="discardEdits">Discard</b-button>
          </template>
        </div>
      </div>

      <!-- transient load failure: offer a retry instead of claiming the
           event is gone. 403/404 fall through to the card below. -->
      <div v-if="!loadingDetail && !model && detailLoadError" class="od-load-error">
        <i class="fa fa-exclamation-triangle"></i>
        <span>The event did not load.</span>
        <b-button size="sm" variant="outline-secondary" @click="load_detail">Retry</b-button>
      </div>

      <!-- not-available card. mirrors the support ticket "not available"
           pattern. ticket may have been deleted or the link is stale. -->
      <div v-if="!loadingDetail && !model && !detailLoadError" class="od-empty-card">
        <i class="fa fa-unlink"></i>
        <div class="od-empty-headline">Event not available</div>
        <div class="od-empty-body">
          This event may have been deleted, or you may not have access to it.
        </div>
        <b-button variant="outline-secondary" size="sm" @click="goBackToList">Back to events</b-button>
      </div>

      <div v-if="loadingDetail" class="od-skeleton">
        <div class="skeleton skeleton-line" style="width: 70%; height: 26px;"></div>
        <div class="skeleton skeleton-line" style="width: 100%; height: 56px; margin-top: 16px;"></div>
        <div class="skeleton skeleton-line" style="width: 100%; height: 18px; margin-top: 24px;"></div>
        <div class="skeleton skeleton-line" style="width: 60%; height: 18px;"></div>
      </div>

      <div v-if="!loadingDetail && model">

        <!-- subject banner: what_happened lifted out of the metadata grid
             with the steelblue content rail, matching the support-ticket
             treatment. read-only in edit mode too: the backend whitelist
             excludes it until FI-1818 wires admin editing. -->
        <div class="od-content-rail od-subject-banner">{{ model.what_happened || '(no details)' }}</div>
        <div v-if="isEditing" class="od-subject-edit-note">Set when the event was logged. Editing is coming in a later update.</div>

        <!-- status workflow chip + transition buttons. only the legal
             next states surface; resolved is reopen-only, gated by
             admin. -->
        <div class="od-status-row">
          <span class="od-status-chip" :class="'od-status-chip--' + model.status">
            <i class="fa" :class="statusIcon(model.status)"></i>
            {{ statusLabel(model.status) }}
          </span>
          <span class="od-status-actions">
            <span class="od-status-move-label" v-if="nextStatusOptions.length">Move to</span>
            <b-button
              v-for="next in nextStatusOptions"
              :key="next"
              variant="outline-secondary"
              size="sm"
              :disabled="saving || isEditing"
              @click="transitionTo(next)"
            >
              <i class="fa" :class="statusIcon(next)"></i>&nbsp;{{ statusActionLabel(next) }}
            </b-button>
          </span>
        </div>
        <div v-if="detailError" class="od-detail-error">{{ detailError }}</div>

        <!-- metadata grid -->
        <table class="od-table">
          <tbody>
            <tr>
              <td class="od-cell">
                <h5>Type</h5>
                <div class="od-value">{{ readableLabel(model.entry_type) }}</div>
              </td>
              <td class="od-cell">
                <h5>Category</h5>
                <div class="od-value">{{ readableLabel(model.category) }}</div>
              </td>
              <td class="od-cell">
                <h5>Submitted by</h5>
                <div class="od-value">{{ displayUserName(model.submitted_by_user_id) }}</div>
              </td>
              <td class="od-cell">
                <h5>Created</h5>
                <div class="od-value">{{ formatDate(model.created_at) }}</div>
              </td>
            </tr>
            <tr>
              <td class="od-cell">
                <h5>Driver</h5>
                <div class="od-value" v-if="!isEditing">{{ model.driver_id || '-' }}</div>
                <b-form-input v-else v-model="edit.driver_id" size="sm" placeholder="Driver ID or name"/>
              </td>
              <td class="od-cell">
                <h5>Vehicle</h5>
                <div class="od-value" v-if="!isEditing">{{ model.vehicle_id || '-' }}</div>
                <b-form-input v-else v-model="edit.vehicle_id" size="sm" placeholder="Vehicle ID"/>
              </td>
              <td class="od-cell">
                <h5>Charter ref</h5>
                <div class="od-value" v-if="!isEditing">{{ model.charter_ref || '-' }}</div>
                <b-form-input v-else v-model="edit.charter_ref" size="sm" placeholder="Charter reference"/>
              </td>
              <td class="od-cell">
                <h5>Assigned</h5>
                <div class="od-value" v-if="!isEditing">{{ displayUserName(model.assigned_user_id) || 'Unassigned' }}</div>
                <template v-else>
                  <b-form-input v-model="edit.assigned_user_id" size="sm" placeholder="Name or email" list="od-assigned-datalist"/>
                  <datalist id="od-assigned-datalist">
                    <option v-for="o in assignedUserOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </datalist>
                </template>
              </td>
            </tr>
            <tr v-if="model.customer_delayed || model.passengers_onboard !== null">
              <td class="od-cell" colspan="2">
                <h5>Customer delayed</h5>
                <div class="od-value">
                  <span v-if="model.customer_delayed === 'yes'">
                    Yes<span v-if="model.delay_duration_min"> ({{ model.delay_duration_min }} min)</span>
                  </span>
                  <span v-else-if="model.customer_delayed === 'no'">No</span>
                  <span v-else-if="model.customer_delayed === 'not_applicable'">N/A</span>
                  <span v-else>-</span>
                </div>
              </td>
              <td class="od-cell" colspan="2">
                <h5>Passengers onboard</h5>
                <div class="od-value">
                  <span v-if="model.passengers_onboard === true">Yes</span>
                  <span v-else-if="model.passengers_onboard === false">No</span>
                  <span v-else>-</span>
                </div>
              </td>
            </tr>
            <tr v-if="model.location && model.location.text">
              <td class="od-cell" colspan="4">
                <h5>Location</h5>
                <div class="od-value">{{ model.location.text }}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- FI-1813 submission details: the per-category fields the
             submitter filled out via the public form. read-only here;
             editing the original submission is intentionally not
             allowed (we want a faithful record of what was reported). -->
        <div class="od-section-card" v-if="extraFieldEntries.length">
          <div class="od-section-header"><h4>Submission details</h4></div>
          <div class="od-section-body">
            <div class="od-extras-grid">
              <div v-for="entry in extraFieldEntries" :key="entry.key" class="od-extra-row">
                <div class="od-extra-label">{{ readableLabel(entry.key) }}</div>
                <div class="od-extra-value">{{ entry.display }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- FI-1820: sos access rides the categories where roadside help
             is likely in play (breakdown/accident), for follow-up
             coordination without leaving the event. -->
        <div class="od-section-card" v-if="showSosCard">
          <div class="od-section-header"><h4>SOS</h4></div>
          <div class="od-section-body od-sos-body">
            <span class="od-sos-text">Coordinate roadside assistance for this event in Bus SOS.</span>
            <b-button variant="outline-danger" size="sm" :to="sosLinkTo">
              <i class="fa fa-exclamation-triangle"></i>&nbsp;Open Bus SOS
            </b-button>
          </div>
        </div>

        <!-- photos (FI-1812). finalized attachments render as a thumb
             gallery; adds stage to s3 then attach explicitly so a stray
             pick can be removed before it becomes part of the record. -->
        <div class="od-section-card">
          <div class="od-section-header"><h4>Photos<span v-if="attachments.length" class="od-photo-count">{{ attachments.length }}</span></h4></div>
          <div class="od-section-body">
            <div v-if="attachments.length" class="od-photo-gallery">
              <div v-for="a in attachments" :key="a.attachment_id" class="od-photo-tile">
                <a :href="a.url" target="_blank" rel="noopener" class="od-photo-thumb" :title="a.name">
                  <img :src="a.url" :alt="a.name"/>
                </a>
                <button type="button" class="od-photo-remove" :title="'Remove ' + a.name" :disabled="!!removingAttachmentId" @click="removePhoto(a)">
                  <i class="fa" :class="removingAttachmentId === a.attachment_id ? 'fa-spinner fa-spin' : 'fa-times'"></i>
                </button>
              </div>
            </div>
            <div v-if="photoRemoveError" class="od-comment-error">{{ photoRemoveError }}</div>
            <div v-if="!attachments.length && !showPhotoAdd" class="od-empty-inline">No photos yet.<b-button variant="link" size="sm" class="od-empty-add" @click="showPhotoAdd = true">+ Add</b-button></div>
            <div v-if="attachments.length && !showPhotoAdd">
              <b-button variant="link" size="sm" class="od-empty-add" @click="showPhotoAdd = true">+ Add photos</b-button>
            </div>
            <div v-if="showPhotoAdd" class="od-photo-add-panel">
              <od-photo-input
                ref="detailPhotoInput"
                :presign="presignPhoto"
                @input="detailPhotoKeys = $event"
                @busy="detailPhotosBusy = $event"
              />
              <div v-if="photoAddError" class="od-comment-error">{{ photoAddError }}</div>
              <div class="od-composer-actions">
                <b-button variant="link" size="sm" :disabled="attachingPhotos" @click="cancelPhotoAdd">Cancel</b-button>
                <b-button variant="primary" size="sm" :disabled="!detailPhotoKeys.length || detailPhotosBusy || attachingPhotos" @click="attachPhotos">
                  <span v-if="attachingPhotos">Attaching...</span>
                  <span v-else-if="detailPhotosBusy">Uploading...</span>
                  <span v-else>Attach {{ detailPhotoKeys.length }} photo{{ detailPhotoKeys.length === 1 ? '' : 's' }}</span>
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- action items + follow-up resolution (editable) -->
        <div class="od-section-card">
          <div class="od-section-header"><h4>Action items</h4></div>
          <div class="od-section-body" v-if="!isEditing">
            <div v-if="model.action_items" class="od-pre">{{ model.action_items }}</div>
            <div v-else class="od-empty-inline">No action items yet.<b-button v-if="!isEditing && model" variant="link" size="sm" class="od-empty-add" @click="enterEditMode('action_items')">+ Add</b-button></div>
          </div>
          <b-form-textarea v-else ref="edit_action_items" v-model="edit.action_items" :rows="3" max-rows="10" no-resize placeholder="Action items..."/>
        </div>

        <div class="od-section-card">
          <div class="od-section-header"><h4>Follow-up &amp; resolution</h4></div>
          <div class="od-section-body" v-if="!isEditing">
            <div v-if="model.follow_up_resolution" class="od-pre">{{ model.follow_up_resolution }}</div>
            <div v-else class="od-empty-inline">No follow-up notes yet.<b-button v-if="!isEditing && model" variant="link" size="sm" class="od-empty-add" @click="enterEditMode('follow_up_resolution')">+ Add</b-button></div>
          </div>
          <b-form-textarea v-else ref="edit_follow_up_resolution" v-model="edit.follow_up_resolution" :rows="3" max-rows="10" no-resize placeholder="Follow-up and resolution..."/>
        </div>

        <!-- admin-only block: preventability + coaching note. cream + gold
             rail per the support-ticket admin treatment. -->
        <div class="od-section-card od-section-card--admin" v-if="canEditAdminFields">
          <div class="od-section-header od-section-header--admin">
            <h4><i class="fa fa-star"></i>&nbsp;Coaching &amp; classification</h4>
          </div>
          <div class="od-section-body" v-if="!isEditing">
            <div class="od-admin-grid">
              <div>
                <h5>Preventability</h5>
                <div class="od-value">{{ readableLabel(model.preventability) }}</div>
              </div>
              <div>
                <h5>Coaching note</h5>
                <div class="od-value" v-if="model.coaching_note">{{ model.coaching_note }}</div>
                <div class="od-empty-inline" v-else>-</div>
              </div>
            </div>
          </div>
          <div class="od-section-body" v-else>
            <label class="od-edit-label" for="od-edit-prev">Preventability</label>
            <b-form-select id="od-edit-prev" v-model="edit.preventability" :options="preventabilityOptions" size="sm"/>
            <label class="od-edit-label" for="od-edit-coach" style="margin-top:10px;">Coaching note</label>
            <b-form-textarea id="od-edit-coach" v-model="edit.coaching_note" :rows="2" max-rows="6" no-resize placeholder="Coaching note (admin only)..."/>
          </div>
        </div>

        <!-- comments (message-board layout) -->
        <div class="od-section-card">
          <div class="od-section-header"><h4>Comments</h4></div>
          <div class="od-section-body">
            <div v-if="!comments.length" class="od-empty-inline">No comments yet.</div>
            <div v-for="c in sortedComments" :key="c.event_comment_sk" class="od-comment">
              <div class="od-comment-avatar" :title="displayUserName(c.author_user_id)">
                {{ initialsFor(c.author_user_id) }}
              </div>
              <div class="od-comment-body">
                <div class="od-comment-head">
                  <span class="od-comment-name">{{ displayUserName(c.author_user_id) }}</span>
                  <span v-if="c.source === 'email_reply'" class="od-comment-via" title="Added by replying to a notification email">
                    <i class="fa fa-envelope-o"></i>&nbsp;via email
                  </span>
                  <span class="od-comment-time" :title="formatDateFull(c.created_at)">
                    {{ formatDate(c.created_at) }}
                  </span>
                </div>
                <div class="od-comment-text">{{ c.body }}</div>
              </div>
            </div>

            <div class="od-comment-composer">
              <b-form-textarea v-model="newComment" :rows="2" max-rows="6" no-resize placeholder="Add a comment..."/>
              <div v-if="commentError" class="od-comment-error">{{ commentError }}</div>
              <div class="od-composer-actions">
                <b-button variant="primary" size="sm" :disabled="!newComment.trim() || posting" @click="postComment">
                  Post comment
                </b-button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- list mode -->
    <div v-else class="od-list">

      <div class="od-rail">
        <div class="od-rail-text">
          <div class="od-rail-eyebrow">Ops · Event Response Manager</div>
          <h3 class="od-rail-title">
            Events
            <span v-if="contextTenantName" class="od-rail-tenant-chip">{{ contextTenantName }}</span>
          </h3>
          <div v-if="!loading" class="od-rail-sub">{{ railCountLabel }}</div>
        </div>
        <div class="od-rail-actions">
          <!-- staff "+ Add entry" affordance. authed counterpart of the
               public /op/<slug> form: same schema-driven fields, json
               body, identity-stamped submitter. -->
          <b-button
            size="sm" variant="primary"
            @click="openAddEntry"
            title="Add a new ERM entry"
          >
            <i class="fa fa-plus"></i>&nbsp;Add entry
          </b-button>
          <!-- FI-1932: csv export of the current filtered view. available to
               every tenant user; the backend strips the coaching columns for
               non-admins. downloads via an authed blob, not window.open. -->
          <b-button
            size="sm" variant="outline-secondary"
            :disabled="loading || exporting"
            @click="exportCsv"
            title="Export the current view to CSV"
          >
            <i class="fa" :class="exporting ? 'fa-spinner fa-spin' : 'fa-download'"></i>&nbsp;Export CSV
          </b-button>
          <!-- FI-1818: tenant admin entry. tucked next to the title rather
               than a top-level nav item so the surface stays focused. -->
          <b-button
            v-if="canEditAdminFields"
            size="sm" variant="outline-secondary"
            @click="openSettings"
            title="Manage ERM settings"
          >
            <i class="fa fa-cog"></i>&nbsp;Settings
          </b-button>
        </div>
      </div>

      <!-- chip filters carry the most-clicked filter dimensions (status,
           type) in pill form — same visual language as the day chips on
           the huddle settings tab. category stays a select because the
           list (21 incident + 5 kudos) is too long for chips; dates +
           search stay as inputs. -->
      <div class="od-chip-filters">
        <div class="od-chip-group" role="group" aria-label="Status">
          <span class="od-chip-group-label">Status</span>
          <button v-for="opt in statusFilterOptions" :key="'s-' + opt.value"
                  type="button"
                  :class="['od-chip', opt.value ? 'od-fc-' + filterChipColor('status', opt.value) : '', { 'od-chip--active': filter_status === opt.value }]"
                  :aria-pressed="filter_status === opt.value ? 'true' : 'false'"
                  @click="setStatusFilter(opt.value)">
            {{ opt.text }}{{ chipCountSuffix('status', opt.value) }}
          </button>
        </div>
        <div class="od-chip-group" role="group" aria-label="Type">
          <span class="od-chip-group-label">Type</span>
          <button v-for="opt in entryTypeFilterOptions" :key="'t-' + opt.value"
                  type="button"
                  :class="['od-chip', opt.value ? 'od-fc-' + filterChipColor('type', opt.value) : '', { 'od-chip--active': filter_entry_type === opt.value }]"
                  :aria-pressed="filter_entry_type === opt.value ? 'true' : 'false'"
                  @click="setEntryTypeFilter(opt.value)">
            {{ opt.text }}{{ chipCountSuffix('type', opt.value) }}
          </button>
        </div>
      </div>

      <div class="od-filter-row">
        <span class="od-filter-field">
          From&nbsp;
          <b-form-input v-model="filter_from" type="date" size="sm" class="od-filter-input" @change="load_data"/>
        </span>
        <span class="od-filter-field">
          To&nbsp;
          <b-form-input v-model="filter_to" type="date" size="sm" class="od-filter-input" @change="load_data"/>
        </span>
        <span class="od-filter-field">
          Category&nbsp;
          <b-form-select v-model="filter_category" :options="categoryFilterOptions" size="sm" class="od-filter-select" @change="load_data"/>
        </span>
        <!-- search pill matching the vsm board: leading magnifier, rounded
             field, clear affordance. searches server-side on enter; a bare
             "#123" jumps straight to that event instead. the date and
             category filters auto-apply on change, so no explicit button. -->
        <span class="od-search">
          <i class="fa fa-search od-search-icon" aria-hidden="true"></i>
          <input
            v-model="filter_search"
            type="text"
            class="od-search-input"
            placeholder="Search events or #123"
            aria-label="Search events, or type #123 to open an event"
            @keyup.enter="onSearchSubmit"
          />
          <button v-if="filter_search" type="button" class="od-search-clear" aria-label="Clear search" @click="clearSearchField"><i class="fa fa-times" aria-hidden="true"></i></button>
        </span>
      </div>

      <!-- FI-1818 ERM settings as a modal (mobile-friendly). the gear + the
           first-run button open it; the /ops_desk_settings route and the
           huddle panel's "manage" link redirect here with ?settings=1. the
           settings content mounts lazily (v-if) so it re-hydrates each open
           and frees its watchers on close. -->
      <b-modal
        id="erm-settings-modal"
        size="xl"
        scrollable
        hide-footer
        modal-class="od-settings-modal"
        @show="settingsModalShown = true"
        @hidden="onSettingsHidden"
      >
        <template slot="modal-title">
          Event Response Manager settings
          <span v-if="contextTenantName" class="od-rail-tenant-chip">{{ contextTenantName }}</span>
        </template>
        <ops-desk-settings
          v-if="settingsModalShown"
          :embedded="true"
          @close="$bvModal.hide('erm-settings-modal')"
        />
      </b-modal>

      <!-- "+ Add entry" modal. authed staff submission. schema-driven
           conditional fields match the public /op/<slug> form's renderer
           so an admin filing in-app sees the same shape a customer would. -->
      <b-modal
        id="add-entry-modal"
        title="Add ERM entry"
        size="lg"
        hide-footer
        no-close-on-backdrop
        @hide="onAddEntryHide"
        @hidden="onAddEntryHidden"
      >
        <div v-if="addEntryLoading" class="od-add-loading">
          <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading form...
        </div>
        <div v-else-if="addEntrySuccess" class="od-add-success">
          <i class="fa fa-check-circle"></i>
          <div class="od-add-success-headline">Entry added</div>
          <div class="od-add-success-body">
            Event #{{ addEntrySuccess.event_id }} logged.
          </div>
          <div class="od-add-actions">
            <b-button variant="link" @click="closeAddEntry">Done</b-button>
            <b-button variant="primary" @click="addAnother">Add another</b-button>
            <b-button variant="outline-secondary" @click="openCreatedEvent">Open event</b-button>
          </div>
        </div>
        <div v-else>
          <div class="od-add-type-row">
            <button v-for="t in addEntryTypeOptions" :key="t.value"
                    type="button"
                    :class="['od-add-type-btn', { 'od-add-type-btn--active': addEntry.entry_type === t.value }]"
                    @click="setAddEntryType(t.value)">
              <i class="fa" :class="t.icon"></i>&nbsp;{{ t.label }}
            </button>
          </div>

          <div class="od-add-field" v-if="addEntryCategoryOptions.length">
            <label for="od-add-category">Category <span class="od-add-req">*</span></label>
            <b-form-select id="od-add-category" v-model="addEntry.category" :options="addEntryCategoryOptions" size="sm"/>
          </div>

          <!-- FI-1820: sos inline on breakdown/accident. opens in a new
               tab so the half-typed entry isn't lost. -->
          <div v-if="showAddEntrySos" class="od-add-sos">
            <span class="od-add-sos-text"><strong>Need roadside help?</strong> You can send an SOS while you log this.</span>
            <router-link :to="addEntrySosLinkTo" target="_blank" class="od-add-sos-btn">
              <i class="fa fa-exclamation-triangle"></i>&nbsp;Open Bus SOS
            </router-link>
          </div>

          <!-- category-driven fields, grouped in the same tinted panel
               the public form uses so the block that changes with the
               category reads as one unit. -->
          <div v-if="addEntryConditionalFields.length" class="od-add-conditional">
            <div v-for="f in addEntryConditionalFields" :key="f.key" class="od-add-field">
              <label :for="'od-add-f-' + f.key">{{ f.label }}<span v-if="f.required" class="od-add-req"> *</span></label>
              <div v-if="f.help" class="od-add-help">{{ f.help }}</div>
              <b-form-textarea v-if="f.type === 'textarea'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" :rows="2" max-rows="6" no-resize :placeholder="f.placeholder || ''"/>
              <b-form-select v-else-if="f.type === 'select'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" :options="selectOptions(f)" size="sm"/>
              <b-form-checkbox v-else-if="f.type === 'checkbox'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" size="sm">{{ f.placeholder || 'Yes' }}</b-form-checkbox>
              <b-form-input v-else-if="f.type === 'number'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="number" size="sm" :placeholder="f.placeholder || ''"/>
              <b-form-input v-else-if="f.type === 'tel'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="tel" size="sm" :placeholder="f.placeholder || ''"/>
              <b-form-input v-else-if="f.type === 'email'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="email" size="sm" :placeholder="f.placeholder || ''"/>
              <b-form-input v-else-if="f.type === 'date'" :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" type="date" size="sm"/>
              <b-form-input v-else :id="'od-add-f-' + f.key" v-model="addEntry.extras[f.key]" size="sm" :placeholder="f.placeholder || ''"/>
            </div>
          </div>

          <div class="od-add-field">
            <label for="od-add-what">What happened <span class="od-add-req">*</span></label>
            <b-form-textarea id="od-add-what" v-model="addEntry.what_happened" :rows="3" max-rows="10" no-resize placeholder="Brief description of the event..."/>
          </div>

          <div class="od-add-row">
            <div class="od-add-col">
              <label for="od-add-driver">Driver</label>
              <b-form-input id="od-add-driver" v-model="addEntry.driver_id" size="sm" placeholder="ID or name"/>
            </div>
            <div class="od-add-col">
              <label for="od-add-vehicle">Vehicle</label>
              <b-form-input id="od-add-vehicle" v-model="addEntry.vehicle_id" size="sm" placeholder="ID"/>
            </div>
            <div class="od-add-col">
              <label for="od-add-charter">Charter ref</label>
              <b-form-input id="od-add-charter" v-model="addEntry.charter_ref" size="sm" placeholder="Optional"/>
            </div>
          </div>

          <div class="od-add-field">
            <label for="od-add-location">Location</label>
            <b-form-input id="od-add-location" v-model="addEntry.location_text" size="sm" placeholder="Optional"/>
          </div>

          <div class="od-add-field">
            <label>Photos</label>
            <od-photo-input
              ref="addEntryPhotoInput"
              :presign="presignPhoto"
              @input="addEntryPhotoKeys = $event"
              @busy="addEntryPhotosBusy = $event"
            />
          </div>

          <div v-if="addEntryError" class="od-add-error">{{ addEntryError }}</div>

          <div class="od-add-actions">
            <b-button v-if="addEntryDirty" variant="link" class="od-add-discard" :disabled="addEntrySubmitting" @click="resetAddEntryForm">Discard draft</b-button>
            <b-button variant="link" :disabled="addEntrySubmitting" @click="closeAddEntry">Cancel</b-button>
            <b-button variant="primary" :disabled="!canSubmitAddEntry || addEntrySubmitting || addEntryPhotosBusy" @click="submitAddEntry">
              <i class="fa fa-plus"></i>&nbsp;<span v-if="addEntrySubmitting">Saving...</span><span v-else-if="addEntryPhotosBusy">Uploading photos...</span><span v-else>Add entry</span>
            </b-button>
          </div>
        </div>
      </b-modal>

      <!-- load failure: an api error must never read as "no events yet".
           replaces the table and the first-run card until a retry lands. -->
      <div v-if="loadError && !loading" class="od-load-error">
        <i class="fa fa-exclamation-triangle"></i>
        <span>Events did not load.</span>
        <b-button size="sm" variant="outline-secondary" @click="load_data">Retry</b-button>
      </div>

      <!-- first-run onboarding: a successful, unfiltered, empty response
           means nothing is in the tenant yet, so a bare "no events match"
           table would read as an error. point at the two ways data gets
           in instead. -->
      <div v-else-if="!loading && !items.length && !hasActiveFilters" class="od-empty-card od-first-run">
        <i class="fa fa-inbox"></i>
        <div class="od-empty-headline">No events yet</div>
        <div class="od-empty-body">Log your first incident, kudos, or note, or share your public form so drivers and staff can report from anywhere.</div>
        <div class="od-first-run-actions">
          <b-button variant="primary" size="sm" @click="openAddEntry"><i class="fa fa-plus"></i>&nbsp;Add entry</b-button>
          <b-button v-if="canEditAdminFields" variant="outline-secondary" size="sm" @click="openSettings"><i class="fa fa-share-alt"></i>&nbsp;Set up the public form</b-button>
        </div>
      </div>

      <div v-else class="od-table-wrap">
        <!-- ≤768px: tap-friendly card list in place of the dense table.
             mirrors support_ticket_list's mobile pattern; each card opens
             the event detail via the same openEvent() the rows use. -->
        <div v-if="isMobileView" class="od-mobile-list">
          <template v-if="loading">
            <div v-for="n in 4" :key="'od-sk-' + n" class="od-mobile-card od-mobile-card--skeleton">
              <div class="od-mobile-card-head">
                <span class="od-skel od-skel--id"></span>
                <span class="od-skel od-skel--chip"></span>
              </div>
              <span class="od-skel od-skel--subject"></span>
              <span v-if="n % 2 === 0" class="od-skel od-skel--subject od-skel--subject-2"></span>
              <div class="od-skel-meta-row">
                <span class="od-skel od-skel--meta"></span>
                <span class="od-skel od-skel--meta"></span>
              </div>
            </div>
          </template>
          <template v-else>
            <button
              v-for="item in mobileSortedItems"
              :key="'od-mbl-' + item.id"
              type="button"
              class="od-mobile-card"
              :aria-label="'Open event #' + item.id"
              @click="openEvent(item)"
            >
              <div class="od-mobile-card-head">
                <span class="od-mobile-card-id">#{{ item.id }}</span>
                <span class="od-mobile-card-chips">
                  <span class="od-type-chip" :class="'od-type-chip--' + item.entry_type">
                    <i class="fa" :class="entryTypeIcon(item.entry_type)"></i>
                    {{ readableLabel(item.entry_type) }}
                  </span>
                  <span class="od-status-chip" :class="'od-status-chip--' + item.status" :title="statusLabel(item.status)">
                    <i class="fa" :class="statusIcon(item.status)"></i>
                    {{ statusShortLabel(item.status) }}
                  </span>
                </span>
              </div>
              <div class="od-mobile-card-subject">{{ truncate(item.what_happened, 300) || '(no description)' }}</div>
              <div class="od-mobile-card-meta">
                <span v-if="item.category" class="od-cat-chip">{{ readableLabel(item.category) }}</span>
                <span v-if="item.driver_id"><i class="fa fa-user od-mobile-meta-icon"></i>{{ item.driver_id }}</span>
                <span v-if="item.vehicle_id"><i class="fa fa-bus od-mobile-meta-icon"></i>{{ item.vehicle_id }}</span>
                <span class="od-mobile-card-dt">{{ formatDateTimeCompact(item.created_at) }}</span>
              </div>
            </button>
            <div v-if="!items.length" class="od-mobile-empty">
              No events match these filters.&nbsp;<button type="button" class="od-clear-btn" @click="clearFilters">Clear filters</button>
            </div>
          </template>
        </div>

        <div v-show="!isMobileView" class="od-table-inline">
          <b-table
            :busy="loading"
            :items="items"
            :fields="listFields"
            sort-by="created_at"
            :sort-desc="true"
            small
            responsive
            show-empty
            @row-clicked="openEvent"
          >
            <!-- bootstrap-vue 2.0.0-rc.28 uses bare-key cell slots
                 (slot="entry_type"), not the later slot="cell(entry_type)"
                 form — matching support_ticket_list.vue. the cell() form is
                 silently ignored here, which is why these chips and the
                 compact date never rendered. -->
            <!-- keyboard path into the detail: row-click stays for mouse
                 users, the id doubles as a real focusable control. -->
            <template slot="id" slot-scope="row">
              <button type="button" class="od-id-link" :aria-label="'Open event #' + row.item.id" @click.stop="openEvent(row.item)">#{{ row.item.id }}</button>
            </template>
            <template slot="entry_type" slot-scope="row">
              <span class="od-type-chip" :class="'od-type-chip--' + row.item.entry_type">
                <i class="fa" :class="entryTypeIcon(row.item.entry_type)"></i>
                {{ readableLabel(row.item.entry_type) }}
              </span>
            </template>
            <template slot="category" slot-scope="row">
              <span class="od-cat-chip" v-if="row.item.category">{{ readableLabel(row.item.category) }}</span>
              <span v-else>-</span>
            </template>
            <template slot="status" slot-scope="row">
              <span class="od-status-chip" :class="'od-status-chip--' + row.item.status" :title="statusLabel(row.item.status)">
                <i class="fa" :class="statusIcon(row.item.status)"></i>
                {{ statusShortLabel(row.item.status) }}
              </span>
            </template>
            <template slot="driver_id" slot-scope="row">{{ row.item.driver_id || '-' }}</template>
            <template slot="vehicle_id" slot-scope="row">{{ row.item.vehicle_id || '-' }}</template>
            <template slot="what_happened" slot-scope="row">
              <!-- css owns the single-line ellipsis; the character cut is
                   only a guard against oversized payloads. -->
              <span class="od-snippet">{{ truncate(row.item.what_happened, 300) }}</span>
            </template>
            <template slot="created_at" slot-scope="row">
              <span class="od-list-dt" :title="row.item.created_at">{{ formatDateTimeCompact(row.item.created_at) }}</span>
            </template>
            <!-- filtered-to-zero: offer the one-click way out. -->
            <template slot="empty">
              <div class="od-table-empty">No events match these filters.&nbsp;<button type="button" class="od-clear-btn" @click="clearFilters">Clear filters</button></div>
            </template>
            <!-- shimmer rows while the list loads, so the first fetch never
                 shows a bare header row. matches the detail skeleton. -->
            <template slot="table-busy">
              <div class="od-busy-rows">
                <div v-for="n in 5" :key="n" class="skeleton skeleton-line od-busy-row"></div>
              </div>
            </template>
          </b-table>
        </div>
        <!-- infinite scroll: the sentinel sits below both list views and
             pulls the next page as it nears the viewport. the spinner shows
             only while a page is in flight; once next_token clears there is
             nothing left to load and neither renders. -->
        <div v-if="loadingMore" class="od-loading-more">
          <span class="od-loading-more-spinner" aria-hidden="true"></span>
          <span>Loading more events…</span>
        </div>
        <div ref="scrollSentinel" class="od-scroll-sentinel" aria-hidden="true"></div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment/moment'
import { formatDateTimeCompact } from '@/helpers/dates'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import {
  list_ops_desk_events,
  export_ops_desk_events,
  get_ops_desk_event,
  add_ops_desk_event_comment,
  transition_ops_desk_event_status,
  update_ops_desk_event,
  create_ops_desk_event,
  get_ops_desk_form_schema,
  presign_ops_desk_attachment,
  add_ops_desk_event_attachments,
  delete_ops_desk_event_attachment,
  get_users,
} from '../networking/apis'
import OdPhotoInput from './od_photo_input.vue'
import OpsDeskSettings from './ops_desk_settings.vue'

// status workflow mirrors the FI-1815 backend _TRANSITIONS table. surface
// the legal next states as buttons; resolved -> in_progress (reopen) is
// admin-only and only shown when the viewer's role qualifies.
const TRANSITIONS = {
  new: ['in_progress', 'awaiting_followup', 'resolved'],
  in_progress: ['awaiting_followup', 'resolved'],
  awaiting_followup: ['in_progress', 'resolved'],
  resolved: ['in_progress'],
}

const STATUS_LABEL = {
  new: 'New',
  in_progress: 'In progress',
  awaiting_followup: 'Awaiting follow-up',
  resolved: 'Resolved',
}

// terse labels for the inline list chip so a status never wraps the row to
// double height (the column is fixed-width and the chip is uppercased).
// the detail view + filters keep the full STATUS_LABEL; the list cell
// carries the full label on a hover title.
const STATUS_SHORT = {
  new: 'New',
  in_progress: 'In progress',
  awaiting_followup: 'Follow-up',
  resolved: 'Resolved',
}

const STATUS_ICON = {
  new: 'fa-circle-o',
  // play-circle reads as "underway"; fa-spinner implies the page is busy.
  in_progress: 'fa-play-circle-o',
  awaiting_followup: 'fa-clock-o',
  resolved: 'fa-check-circle',
}

const STATUS_ACTION = {
  new: 'Mark new',
  in_progress: 'Start',
  awaiting_followup: 'Awaiting follow-up',
  resolved: 'Resolve',
}

// infinite scroll: the server pages the history and hands back a next_token.
// we fetch a page at a time and append, so there is no client-side row cap.
const PAGE_LIMIT = 100

export default {
  name: 'ops_desk',
  components: { OdPhotoInput, OpsDeskSettings },
  data() {
    return {
      loading: false,
      loadingDetail: false,
      saving: false,
      posting: false,
      // FI-1932: guards the export button + swaps its icon to a spinner while
      // the filtered csv drains and downloads.
      exporting: false,
      loadError: false,
      detailLoadError: false,
      detailError: '',
      commentError: '',
      items: [],
      // unfiltered aggregate counts from the list response (total + per
      // status/type); older payloads omit them, so chips null-check.
      counts: null,
      // infinite scroll: cursor for the next page (null once exhausted) and
      // the append-in-flight guard so scroll + resize can't double-fetch.
      nextToken: null,
      loadingMore: false,
      // mounts the settings component only while the settings modal is open.
      settingsModalShown: false,
      // lazy tenant roster backing the assigned datalist in edit mode
      tenantUsers: [],
      tenantUsersLoaded: false,
      tenantUsersLoading: false,
      model: null,
      comments: [],
      attachments: [],
      newComment: '',
      isEditing: false,
      edit: {},
      // eslint-disable-next-line vue/no-reserved-keys
      _originalEdit: {},

      // filters
      filter_from: '',
      filter_to: '',
      filter_status: '',
      filter_category: '',
      filter_entry_type: '',
      filter_search: '',

      // ≤768px swaps the dense list b-table for a tap-friendly card list,
      // matching support_ticket_list / booking_customer_list. kept in sync
      // by a resize listener wired in mounted().
      isMobileView: false,

      statusFilterOptions: [
        { value: '', text: 'All' },
        { value: 'new', text: 'New' },
        { value: 'in_progress', text: 'In progress' },
        { value: 'awaiting_followup', text: 'Awaiting follow-up' },
        { value: 'resolved', text: 'Resolved' },
      ],
      entryTypeFilterOptions: [
        { value: '', text: 'All' },
        { value: 'incident', text: 'Incident' },
        { value: 'kudos', text: 'Kudos' },
        { value: 'note', text: 'Note' },
      ],
      // mirrors INCIDENT_CATEGORIES in ops_desk_service. tenant-specific
      // overrides will surface from a config api in FI-1818.
      categoryFilterOptions: [
        { value: '', text: 'All' },
        ...[
          'accident', 'breakdown', 'delay', 'driver_error', 'driver_question',
          'vehicle_readiness', 'vehicle_issue', 'customer_complaint',
          'passenger_issue', 'hours_of_service', 'weather', 'traffic',
          'route_change', 'fuel_issue', 'dispatch_error',
          'outside_service_request', 'near_miss', 'property_damage', 'injury',
          'lost_and_found', 'other',
          'positive_customer_experience', 'above_and_beyond',
          'safety_recognition', 'team_recognition',
        ].map(value => ({ value, text: value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) })),
      ],
      preventabilityOptions: [
        { value: 'unclassified', text: 'Unclassified' },
        { value: 'preventable', text: 'Preventable' },
        { value: 'not_preventable', text: 'Not preventable' },
        { value: 'absence', text: 'Absence' },
        { value: 'recognition', text: 'Recognition' },
      ],

      listFields: [
        { key: 'id', label: 'ID', sortable: true, thStyle: { width: '64px' } },
        { key: 'entry_type', label: 'Type', sortable: true, thStyle: { width: '90px' } },
        { key: 'category', label: 'Category', sortable: true, thStyle: { width: '140px' } },
        { key: 'status', label: 'Status', sortable: true, thStyle: { width: '160px' } },
        { key: 'driver_id', label: 'Driver', sortable: true, thClass: 'od-driver-cell', tdClass: 'od-driver-cell' },
        { key: 'vehicle_id', label: 'Vehicle', sortable: true, thStyle: { width: '110px' } },
        { key: 'what_happened', label: 'What happened', sortable: false, tdClass: 'od-snippet-cell' },
        { key: 'created_at', label: 'Created', sortable: true, thStyle: { width: '140px' } },
      ],

      // FI-1815 "+ Add entry" modal state. schemas are fetched on first
      // open and cached so re-opens don't pay another round-trip.
      addEntrySchemaLoaded: false,
      addEntryLoading: false,
      addEntrySubmitting: false,
      addEntryError: '',
      addEntrySuccess: null,
      addEntrySchemas: { incident: {}, kudos: {}, note: {} },
      addEntryAvailableCategories: { incident: [], kudos: [], note: [] },
      addEntryTypeOptions: [
        { value: 'incident', label: 'Incident', icon: 'fa-exclamation-circle' },
        { value: 'kudos',    label: 'Kudos',    icon: 'fa-star' },
        { value: 'note',     label: 'Note',     icon: 'fa-sticky-note-o' },
      ],
      addEntry: {
        entry_type: 'incident',
        category: '',
        what_happened: '',
        driver_id: '',
        vehicle_id: '',
        charter_ref: '',
        location_text: '',
        extras: {},
      },
      // FI-1812 photos. add-entry keys ride the create payload; the
      // detail-page picker stages then explicitly attaches so a stray
      // pick can be removed before it becomes part of the record.
      addEntryPhotoKeys: [],
      addEntryPhotosBusy: false,
      showPhotoAdd: false,
      detailPhotoKeys: [],
      detailPhotosBusy: false,
      attachingPhotos: false,
      photoAddError: '',
      photoRemoveError: '',
      removingAttachmentId: '',
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // mobile card list: mirror the b-table's default sort (created_at desc)
    // and the same current-page slice, so cards and table agree on order and
    // paging. moment handles both iso strings and epoch values.
    mobileSortedItems() {
      return this.items.slice().sort((a, b) => {
        const av = a.created_at ? moment(a.created_at).valueOf() : 0
        const bv = b.created_at ? moment(b.created_at).valueOf() : 0
        if (bv !== av) return bv - av
        return (b.id || 0) - (a.id || 0)
      })
    },
    // admin deep-link context: name the tenant being rendered whenever it
    // differs from the navbar selection, so the page owns its context.
    contextTenantName() {
      return tenantContextDiffers(this.$route, this.user) ? tenantDisplayName(this.user, this.tenant) : ''
    },
    event_id() {
      const raw = this.$route.query.event_id
      const n = raw === undefined ? null : parseInt(raw, 10)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    nextStatusOptions() {
      if (!this.model) return []
      const next = TRANSITIONS[this.model.status] || []
      // resolved -> in_progress (reopen) is admin only
      if (this.model.status === 'resolved' && !this.canEditAdminFields) return []
      return next
    },
    canEditAdminFields() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin'
    },
    // FI-1820: sos surfacing. features are keyed by tenant and drop
    // explicit-false flags, so a plain truthy check is the whole gate.
    sosEnabled() {
      const f = this.user && this.user.features && this.user.features[this.tenant]
      return !!(f && f.bus_sos_enabled)
    },
    showSosCard() {
      return this.sosEnabled && !!this.model
        && this.model.entry_type === 'incident'
        && (this.model.category === 'breakdown' || this.model.category === 'accident')
    },
    sosLinkTo() {
      const m = this.model || {}
      const loc = m.location && m.location.text
      const q = { notes: 'ERM entry #' + m.id + (loc ? ' - ' + loc : '') }
      if (m.vehicle_id) q.vehicle = m.vehicle_id
      return { name: 'sos_list', query: q }
    },
    showAddEntrySos() {
      return this.sosEnabled
        && this.addEntry.entry_type === 'incident'
        && (this.addEntry.category === 'breakdown' || this.addEntry.category === 'accident')
    },
    addEntrySosLinkTo() {
      const q = {}
      if (this.addEntry.vehicle_id) q.vehicle = this.addEntry.vehicle_id
      if (this.addEntry.location_text) q.notes = this.addEntry.location_text
      return { name: 'sos_list', query: q }
    },
    // distinguishes a genuinely empty tenant (first-run onboarding card)
    // from a filter combination that matched nothing (clear-filters line).
    hasActiveFilters() {
      return !!(this.filter_status || this.filter_entry_type || this.filter_from || this.filter_to || this.filter_category || (this.filter_search && this.filter_search.trim()))
    },
    // rail subtitle. with infinite scroll the loaded rows grow as the user
    // scrolls, so name how many are loaded against the honest tenant total.
    railCountLabel() {
      const n = this.items.length
      const total = this.counts && typeof this.counts.total === 'number' ? this.counts.total : null
      if (this.hasActiveFilters) {
        // counts.total covers the whole tenant, not this filtered subset, so
        // report the loaded matches and flag that more may follow.
        if (this.nextToken) return n + '+ matching events'
        return n + (n === 1 ? ' matching event' : ' matching events')
      }
      if (total !== null && this.nextToken && total > n) {
        return 'showing ' + n.toLocaleString() + ' of ' + total.toLocaleString() + ' events'
      }
      if (total !== null) return total.toLocaleString() + (total === 1 ? ' event' : ' events')
      return n.toLocaleString() + (n === 1 ? ' event' : ' events')
    },
    // tenant roster formatted for the assigned <datalist>. the username
    // (what the backend stores) is the value; the name rides as the label.
    assignedUserOptions() {
      return (this.tenantUsers || []).map(u => {
        const username = (u.username || '').trim()
        const name = [u.first_name, u.last_name].map(p => (p || '').trim()).filter(Boolean).join(' ')
        return { value: username, label: name }
      }).filter(o => !!o.value)
    },
    // a draft worth guarding: any typed content or a chosen category.
    // entry_type alone doesn't count — picking a tab isn't authoring.
    addEntryDirty() {
      const e = this.addEntry
      if (!e) return false
      if (e.category || (e.what_happened && e.what_happened.trim())) return true
      if (e.driver_id || e.vehicle_id || e.charter_ref || e.location_text) return true
      const extras = e.extras || {}
      return Object.keys(extras).some(k => extras[k] !== '' && extras[k] !== null && extras[k] !== undefined && extras[k] !== false)
    },
    sortedComments() {
      return [...this.comments].sort((a, b) => {
        return (a.created_at || '').localeCompare(b.created_at || '')
      })
    },
    extraFieldEntries() {
      // FI-1813 flatten model.extra_fields into rows for the submission-
      // details card. select-type values arrive as the raw enum key
      // (e.g. "safety"); render them via readableLabel so the operator
      // sees "Safety" not "safety". checkbox-type values come through
      // as booleans; render Yes/No.
      const extras = (this.model && this.model.extra_fields) || {}
      return Object.entries(extras)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([key, value]) => {
          let display
          if (typeof value === 'boolean') display = value ? 'Yes' : 'No'
          else if (typeof value === 'string' && /^[a-z0-9_]+$/.test(value)) display = this.readableLabel(value)
          else display = String(value)
          return { key, display }
        })
    },
    // FI-1815 category options for the "+ Add entry" modal, filtered to the
    // tenant's enabled set for the chosen entry type. note entry-type has
    // no categories — submitter uses what_happened for everything.
    addEntryCategoryOptions() {
      const t = this.addEntry.entry_type
      const cats = (this.addEntryAvailableCategories && this.addEntryAvailableCategories[t]) || []
      return [
        { value: '', text: t === 'note' ? '(No category needed)' : 'Pick a category...', disabled: t !== 'note' },
        ...cats.map(c => ({ value: c, text: this.readableLabel(c) })),
      ]
    },
    // per-(type, category) fields from the schema payload. the api
    // pre-merges entry-type-level fields into every category bucket, so
    // a category hit already carries them; _default covers the
    // no-category case (kudos before a pick, note always). mirrors the
    // public form's conditionalFields.
    addEntryConditionalFields() {
      const t = this.addEntry.entry_type
      const c = this.addEntry.category
      const bucket = (this.addEntrySchemas && this.addEntrySchemas[t]) || {}
      return (c && bucket[c]) || bucket._default || []
    },
    canSubmitAddEntry() {
      const t = this.addEntry.entry_type
      const needsCategory = t !== 'note'
      if (needsCategory && !this.addEntry.category) return false
      if (!this.addEntry.what_happened || !this.addEntry.what_happened.trim()) return false
      for (const f of this.addEntryConditionalFields) {
        if (!f.required) continue
        const v = this.addEntry.extras[f.key]
        if (v === undefined || v === null || v === '') return false
      }
      return true
    },
  },

  watch: {
    event_id(newVal) {
      if (newVal) this.load_detail()
      else {
        this.model = null; this.comments = []; this.attachments = []
        this.cancelPhotoAdd()
      }
    },
    // deep-link / redirect target: /ops_desk_settings and the huddle panel's
    // "manage" link redirect to ?settings=1, which opens the settings modal.
    '$route.query.settings'() { this.syncSettingsModal() },
    // seed select extras to '' so the placeholder option displays as
    // selected; an undefined v-model matches no option and the control
    // renders blank.
    addEntryConditionalFields(fields) {
      (fields || []).forEach(f => {
        if (f.type === 'select' && this.addEntry.extras[f.key] === undefined) {
          this.$set(this.addEntry.extras, f.key, '')
        }
      })
    },
    // tenant can change via the navbar tenant switcher without remounting
    // the component, so re-fetch the current mode's data when it does.
    // also covers the case where a stale huddle/event nc card switches
    // the route from /ops_desk?tenant=A to /ops_desk?tenant=B — vue
    // router reuses the same instance so the mount guard wouldn't fire.
    tenant() {
      if (!this.ensureTenantAccessible()) return
      // the assigned datalist roster is tenant-scoped
      this.tenantUsers = []
      this.tenantUsersLoaded = false
      if (this.event_id) this.load_detail()
      else this.load_data()
    },
  },

  mounted() {
    // deep-link contract with the Today board: ?search= pre-filters the
    // list (the events chip lands here scoped to its vehicle).
    if (this.$route.query.search) this.filter_search = String(this.$route.query.search)
    // FI-1816 huddle nc cards embed the firing tenant in the link. if the
    // user's current jwt tenant_options doesn't include it (smoke seeds,
    // a tenant the user was removed from, a deep link from someone else's
    // share), gracefully fall back to the user's own tenant instead of
    // letting list_ops_desk_events return 403, which networking/index.js
    // turns into a forced localStorage.clear() + /#/login bounce.
    if (!this.ensureTenantAccessible()) return
    if (this.event_id) {
      this.load_detail()
    } else {
      this.load_data()
    }
    // open settings when arriving via ?settings=1 (the redirected
    // /ops_desk_settings route or the huddle panel's "manage" link).
    this.$nextTick(() => this.syncSettingsModal())
    // list card-vs-table switch. window.innerWidth is the proven-reliable
    // breakpoint source on ios (see support_ticket_list).
    this.isMobileView = window.innerWidth <= 768
    this._onListResize = () => {
      this.isMobileView = window.innerWidth <= 768
      // a taller viewport can expose the sentinel; keep the scroll fed.
      this._maybeLoadMore()
    }
    window.addEventListener('resize', this._onListResize)
    // infinite scroll trigger. rAF-throttled so a fast scroll coalesces to
    // one geometry check per frame instead of one per scroll event.
    this._onListScroll = () => {
      if (this._scrollTick) return
      this._scrollTick = true
      window.requestAnimationFrame(() => {
        this._scrollTick = false
        this._maybeLoadMore()
      })
    }
    window.addEventListener('scroll', this._onListScroll, { passive: true })
    // FI-1932: scope the print stylesheet to this page only while it is
    // mounted (mirrors the trip-sheet / vsm-board print pattern), so a print
    // from anywhere else in the app is untouched.
    document.body.classList.add('ops-desk-print')
  },

  beforeDestroy() {
    if (this._onListResize) window.removeEventListener('resize', this._onListResize)
    if (this._onListScroll) window.removeEventListener('scroll', this._onListScroll)
    document.body.classList.remove('ops-desk-print')
  },

  // unsaved detail edits must not silently evaporate: the back arrow,
  // browser back, and navbar tenant switches all route through these.
  beforeRouteUpdate(to, from, next) {
    if (!this.confirmDiscardEdits()) return next(false)
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (!this.confirmDiscardEdits()) return next(false)
    next()
  },

  methods: {
    // FI-1816 pre-flight guard: confirm the user's jwt covers the tenant
    // in the route before any tenant-scoped api fires. mirrors the
    // backend `tenant_user_required` check so we can fall back cleanly
    // instead of cascading through a 403 + login redirect. returns true
    // when access is fine; returns false (and reroutes) when it isn't.
    ensureTenantAccessible() {
      const urlTenant = this.$route.query.tenant
      if (!urlTenant) return true
      const u = this.user
      if (!u) return true
      const opts = Array.isArray(u.tenant_options) ? u.tenant_options : []
      if (opts.length) {
        const found = opts.some(o => (o && (o.value === urlTenant)) || o === urlTenant)
        if (found) return true
      } else if (u.tenant === urlTenant) {
        return true
      }
      const fallback = u.tenant
      if (!fallback || fallback === urlTenant) return true
      const q = { ...this.$route.query, tenant: fallback }
      delete q.event_id
      try {
        this.$bvToast.toast(
          'That notification points to an account outside your access, so we are showing your own Event Response Manager instead.',
          { title: 'Account changed', variant: 'warning', solid: true, autoHideDelay: 5000 },
        )
      } catch (_) { /* toast plugin may not be ready on first paint */ }
      this.$router.replace({ name: 'ops_desk', query: q }).catch(() => {})
      return false
    },

    // the shared filter payload. load_data sends it without a cursor (fresh
    // first page); load_more adds the next_token to resume the same query.
    _listParams() {
      return {
        status: this.filter_status || undefined,
        category: this.filter_category || undefined,
        entry_type: this.filter_entry_type || undefined,
        from: this.filter_from || undefined,
        to: this.filter_to || undefined,
        search: this.filter_search || undefined,
        limit: PAGE_LIMIT,
      }
    },

    // FI-1932: download the current filtered view as csv. reuses the active
    // filter state (minus the page limit and cursor) so the file matches what
    // the user sees. streams an authed blob and clicks a temporary anchor so
    // the jwt rides the normal Authorization header rather than a bare url.
    async exportCsv() {
      if (!this.tenant || this.exporting) return
      this.exporting = true
      try {
        const p = this._listParams()
        const filters = {
          status: p.status,
          category: p.category,
          entry_type: p.entry_type,
          from: p.from,
          to: p.to,
          search: p.search,
        }
        const resp = await export_ops_desk_events(this.tenant, filters)
        const filename = this._exportFilename(resp) || ('erm-events-' + this.tenant + '.csv')
        const url = window.URL.createObjectURL(resp.data)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk exportCsv failed', e)
        this.toast('The export did not complete. Try again.')
      } finally {
        this.exporting = false
      }
    },

    // pull the server-set filename out of content-disposition so the download
    // keeps the erm-events-<tenant>-<date>.csv name. axios lowercases headers.
    _exportFilename(resp) {
      const cd = resp && resp.headers && resp.headers['content-disposition']
      if (!cd) return null
      const m = /filename="?([^"]+)"?/.exec(cd)
      return m ? m[1] : null
    },

    // FI-1932: the print stylesheet (body.ops-desk-print) hides the app chrome
    // and every screen-only control, leaving a clean single-column event.
    printDetail() {
      window.print()
    },

    async load_data() {
      if (!this.tenant) return
      this.loading = true
      // a fresh query resets the scroll: drop any prior cursor and rows so a
      // stale next_token can't append the old query's next page.
      this.nextToken = null
      try {
        const { value: { data = {} } = {} } = await list_ops_desk_events(this.tenant, this._listParams()) || {}
        this.items = data.items || []
        this.counts = data.counts || null
        this.nextToken = data.next_token || null
        this.loadError = false
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk load_data failed', e)
        this.items = []
        this.nextToken = null
        this.loadError = true
      } finally {
        this.loading = false
        // if the first page didn't fill the viewport, keep pulling until it
        // does (or the history runs out), so a short list still reaches the
        // sentinel-driven loop.
        this.$nextTick(this._maybeLoadMore)
      }
    },

    // appends the next page in place. guarded so overlapping scroll/resize
    // triggers and the initial load can't fire concurrent fetches.
    async load_more() {
      if (!this.tenant || !this.nextToken || this.loadingMore || this.loading) return
      this.loadingMore = true
      const token = this.nextToken
      try {
        const params = { ...this._listParams(), next_token: token }
        const { value: { data = {} } = {} } = await list_ops_desk_events(this.tenant, params) || {}
        // ignore a page that raced a filter change (the cursor moved on).
        if (this.nextToken !== token) return
        this.items = this.items.concat(data.items || [])
        this.nextToken = data.next_token || null
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk load_more failed', e)
        // stop the scroll rather than hammer a failing cursor; the loaded
        // rows stay put.
        this.nextToken = null
      } finally {
        this.loadingMore = false
        this.$nextTick(this._maybeLoadMore)
      }
    },

    // geometry-gated trigger shared by the scroll listener, the resize
    // listener, and the post-load recheck. one guarded entry point means the
    // sentinel can never kick off two fetches at once.
    _maybeLoadMore() {
      if (!this.nextToken || this.loadingMore || this.loading) return
      const sentinel = this.$refs.scrollSentinel
      if (!sentinel) return
      // prefetch ~600px before the sentinel reaches the fold so the next
      // page is usually in hand before the user hits the bottom.
      if (sentinel.getBoundingClientRect().top <= window.innerHeight + 600) this.load_more()
    },

    async load_detail() {
      if (!this.tenant || !this.event_id) return
      this.loadingDetail = true
      this.model = null
      this.comments = []
      this.attachments = []
      this.detailLoadError = false
      this.detailError = ''
      this.commentError = ''
      try {
        const { value: { data = {} } = {} } = await get_ops_desk_event(this.tenant, this.event_id) || {}
        if (data && data.event) {
          this.model = data.event
          this.comments = data.comments || []
          this.attachments = data.attachments || []
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk load_detail failed', e)
        this.model = null
        // 403/404 mean the event genuinely is not reachable for this
        // viewer; anything else gets the retry card, not a "not
        // available" claim the system cannot back.
        const status = e && e.response && e.response.status
        this.detailLoadError = status !== 403 && status !== 404
      } finally {
        this.loadingDetail = false
      }
    },

    openEvent(row) {
      this.$router.push({
        name: 'ops_desk',
        query: { ...this.$route.query, tenant: this.tenant, event_id: row.id },
      }).catch(() => {})
    },

    // enter in the search pill. a bare "#123" (or "# 123") is treated as a
    // jump to that event rather than a text query, so a dispatcher can punch
    // in a reference number and land on it. everything else — including a
    // plain number like a charter id from the Today-board deep link — stays
    // a server-side text search.
    onSearchSubmit() {
      const raw = (this.filter_search || '').trim()
      const m = raw.match(/^#\s*(\d+)$/)
      if (m) {
        this.openEventById(m[1])
        return
      }
      this.load_data()
    },
    // opens an event by id, clearing the search term so returning to the
    // list does not leave a "#123" query that would match no rows.
    openEventById(id) {
      this.filter_search = ''
      this.$router.push({
        name: 'ops_desk',
        query: { ...this.$route.query, tenant: this.tenant, event_id: id },
      }).catch(() => {})
    },

    // chip-filter setters: clicking a chip both flips the bound filter
    // and triggers a re-query so the list reacts immediately. matches
    // how the day chips on huddle settings feel.
    setStatusFilter(value) {
      if (this.filter_status === value) return
      this.filter_status = value
      this.load_data()
    },
    setEntryTypeFilter(value) {
      if (this.filter_entry_type === value) return
      this.filter_entry_type = value
      this.load_data()
    },
    clearFilters() {
      this.filter_status = ''
      this.filter_entry_type = ''
      this.filter_from = ''
      this.filter_to = ''
      this.filter_category = ''
      this.filter_search = ''
      this.load_data()
    },
    // clears just the search field (the pill's ✕) and re-queries so the term
    // drops from the results immediately.
    clearSearchField() {
      if (!this.filter_search) return
      this.filter_search = ''
      this.load_data()
    },
    // maps a status/type filter value to the color token its idle-outline /
    // active-fill pill uses, echoing the row chip palette the way the vsm
    // board's filter chips do. returns one of: gray, blue, amber, green, red.
    filterChipColor(kind, value) {
      if (kind === 'status') {
        return {
          new: 'gray',
          in_progress: 'blue',
          awaiting_followup: 'amber',
          resolved: 'green',
        }[value] || 'gray'
      }
      return { incident: 'red', kudos: 'amber', note: 'gray' }[value] || 'gray'
    },

    goBackToList() {
      const q = { ...this.$route.query }
      delete q.event_id
      this.$router.replace({ name: 'ops_desk', query: q }).catch(() => {})
    },

    enterEditMode(focusKey) {
      if (!this.model) return
      // what_happened stays out of the edit object: it is read-only after
      // creation (see the whitelist note in saveEdits).
      this.edit = {
        driver_id: this.model.driver_id || '',
        vehicle_id: this.model.vehicle_id || '',
        charter_ref: this.model.charter_ref || '',
        assigned_user_id: this.model.assigned_user_id || '',
        action_items: this.model.action_items || '',
        follow_up_resolution: this.model.follow_up_resolution || '',
        preventability: this.model.preventability || 'unclassified',
        coaching_note: this.model.coaching_note || '',
      }
      this._originalEdit = JSON.parse(JSON.stringify(this.edit))
      this.isEditing = true
      this.loadTenantUsers()
      // the section "+ add" ghosts pass their field key so the caret lands
      // in the matching textarea once edit mode has rendered.
      if (focusKey) {
        this.$nextTick(() => {
          const r = this.$refs['edit_' + focusKey]
          if (r && r.focus) r.focus()
        })
      }
    },

    discardEdits() {
      this.edit = JSON.parse(JSON.stringify(this._originalEdit || {}))
      this.isEditing = false
    },

    // route-guard hook: true means navigation may proceed. exits edit
    // mode on the way out so a later detail open starts clean.
    confirmDiscardEdits() {
      if (!this.isEditing) return true
      const dirty = JSON.stringify(this.edit) !== JSON.stringify(this._originalEdit || {})
      if (dirty && !window.confirm('Discard unsaved changes?')) return false
      this.isEditing = false
      return true
    },

    async saveEdits() {
      if (!this.model) return
      this.saving = true
      try {
        // what_happened is NOT in the backend whitelist, so it is not part
        // of the edit payload. it's read-only on the detail page after
        // creation (the edit-mode banner note says so) until FI-1818
        // wires it in for the admin tier.
        const payload = {
          driver_id: this.edit.driver_id,
          vehicle_id: this.edit.vehicle_id,
          charter_ref: this.edit.charter_ref,
          assigned_user_id: this.edit.assigned_user_id,
          action_items: this.edit.action_items,
          follow_up_resolution: this.edit.follow_up_resolution,
          preventability: this.edit.preventability,
          coaching_note: this.edit.coaching_note,
        }
        const { value: { data = {} } = {} } = await update_ops_desk_event(this.tenant, this.model.id, payload) || {}
        if (data && data.id) {
          this.model = data
          this.isEditing = false
          this.detailError = ''
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk saveEdits failed', e)
        this.detailError = 'The changes did not save. Try again.'
        this.toast('The changes did not save. Try again.')
      } finally {
        this.saving = false
      }
    },

    async transitionTo(status) {
      if (!this.model) return
      this.saving = true
      try {
        const { value: { data = {} } = {} } = await transition_ops_desk_event_status(this.tenant, this.model.id, status) || {}
        if (data && data.id) {
          this.model = data
          this.detailError = ''
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk transitionTo failed', e)
        this.detailError = 'The status update did not save. Try again.'
        this.toast('The status update did not save. Try again.')
      } finally {
        this.saving = false
      }
    },

    async postComment() {
      const body = (this.newComment || '').trim()
      if (!body || !this.model) return
      this.posting = true
      try {
        const { value: { data = {} } = {} } = await add_ops_desk_event_comment(this.tenant, this.model.id, body) || {}
        if (data && data.body) this.comments.push(data)
        this.newComment = ''
        this.commentError = ''
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk postComment failed', e)
        this.commentError = 'The comment did not save. Try again.'
        this.toast('The comment did not save. Try again.')
      } finally {
        this.posting = false
      }
    },

    toast(message) {
      try {
        this.$bvToast.toast(message, { title: 'Event Response Manager', variant: 'warning', solid: true, autoHideDelay: 4000 })
      } catch (_) { /* toast plugin may not be ready on first paint */ }
    },

    // unfiltered aggregate counts from the list response. older payloads
    // omit counts, in which case chips render their bare labels.
    chipCountSuffix(kind, value) {
      const c = this.counts
      if (!c) return ''
      const n = value ? ((kind === 'status' ? c.status : c.type) || {})[value] : c.total
      return typeof n === 'number' ? ' (' + n + ')' : ''
    },

    // lazy tenant roster for the assigned datalist. one fetch per tenant;
    // failures fall back silently to free-text entry.
    async loadTenantUsers() {
      if (this.tenantUsersLoaded || this.tenantUsersLoading || !this.tenant) return
      this.tenantUsersLoading = true
      try {
        const res = await get_users(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        this.tenantUsers = Array.isArray(data.results) ? data.results : []
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk loadTenantUsers failed', e)
      } finally {
        this.tenantUsersLoaded = true
        this.tenantUsersLoading = false
      }
    },

    readableLabel(v) {
      if (!v) return '-'
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    statusLabel(s) { return STATUS_LABEL[s] || this.readableLabel(s) },
    statusShortLabel(s) { return STATUS_SHORT[s] || this.statusLabel(s) },
    statusIcon(s) { return STATUS_ICON[s] || 'fa-circle-o' },
    statusActionLabel(s) { return STATUS_ACTION[s] || this.statusLabel(s) },
    entryTypeIcon(t) {
      if (t === 'incident') return 'fa-exclamation-circle'
      if (t === 'kudos') return 'fa-star'
      if (t === 'note') return 'fa-sticky-note-o'
      return 'fa-circle-o'
    },

    truncate(s, n) {
      if (!s) return ''
      return s.length > n ? s.slice(0, n - 1) + '...' : s
    },

    formatDate(iso) {
      if (!iso) return ''
      const m = moment(iso)
      // events get revisited months later; only this year's dates may
      // drop the year.
      return m.year() === moment().year() ? m.format('MMM D, h:mm A') : m.format('MMM D, YYYY, h:mm A')
    },

    // compact list-cell timestamp ("6/6 9:30a"), shared with the support
    // list via helpers/dates so both surfaces format dates identically.
    // detail-view cells still use formatDate's longer form.
    formatDateTimeCompact,

    formatDateFull(iso) {
      if (!iso) return ''
      return moment(iso).format('ddd, MMM D, YYYY · h:mm A')
    },

    displayUserName(username) {
      if (!username) return ''
      // simple display: drop the domain on @ addresses. richer mapping
      // (e.g. via user_options lookup) lands when distribution-config
      // edit ui ships (FI-1818).
      const at = username.indexOf('@')
      return at > 0 ? username.slice(0, at) : username
    },

    initialsFor(username) {
      if (!username) return '?'
      const local = username.split('@')[0]
      const parts = local.split(/[._\-+]/).filter(Boolean)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return local.slice(0, 2).toUpperCase()
    },

    // FI-1815 "+ Add entry" modal lifecycle. the form is NOT reset on
    // open: a half-filled draft survives close/reopen and clears only
    // after a successful submit or an explicit discard.
    // ERM settings modal. gated to admin/customer-admin (canEditAdminFields),
    // mirroring the old /ops_desk_settings route guard. the ?settings=1 query
    // keeps the open state shareable and back-button friendly.
    openSettings() {
      if (!this.canEditAdminFields) return
      const q = { ...this.$route.query, tenant: this.tenant, settings: '1' }
      this.$router.replace({ name: 'ops_desk', query: q }).catch(() => {})
      this.$bvModal.show('erm-settings-modal')
    },
    syncSettingsModal() {
      const want = String(this.$route.query.settings || '') === '1'
      if (want) {
        if (this.canEditAdminFields) {
          this.$bvModal.show('erm-settings-modal')
        } else {
          // strip a settings flag the viewer isn't allowed to act on.
          const q = { ...this.$route.query }; delete q.settings
          this.$router.replace({ name: 'ops_desk', query: q }).catch(() => {})
        }
      } else {
        this.$bvModal.hide('erm-settings-modal')
      }
    },
    onSettingsHidden() {
      this.settingsModalShown = false
      // return to a clean list URL once the modal closes.
      if (this.$route.query.settings) {
        const q = { ...this.$route.query }; delete q.settings
        this.$router.replace({ name: 'ops_desk', query: q }).catch(() => {})
      }
    },

    async openAddEntry() {
      if (!this.tenant) return
      this.addEntryError = ''
      this.addEntrySuccess = null
      this.$bvModal.show('add-entry-modal')
      if (this.addEntrySchemaLoaded) return
      this.addEntryLoading = true
      try {
        const res = await get_ops_desk_form_schema(this.tenant)
        const data = (res && res.value && res.value.data) || {}
        this.addEntryAvailableCategories = data.categories || { incident: [], kudos: [], note: [] }
        // the payload nests the per-type buckets one level down, under
        // schemas.entry_types (see field_schemas.all_schemas)
        this.addEntrySchemas = (data.schemas && data.schemas.entry_types) || { incident: {}, kudos: {}, note: {} }
        this.addEntrySchemaLoaded = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk openAddEntry schema load failed', e)
        this.addEntryError = 'Could not load the form. Try again in a moment.'
      } finally {
        this.addEntryLoading = false
      }
    },

    closeAddEntry() {
      this.$bvModal.hide('add-entry-modal')
    },

    // esc / header-x can eat a half-typed entry on one keystroke, so a
    // dirty form asks first; confirming discards it. the footer Cancel
    // closes without a prompt and keeps the draft for the next open.
    onAddEntryHide(evt) {
      const trigger = evt && evt.trigger
      if ((trigger === 'esc' || trigger === 'headerclose') && this.addEntryDirty) {
        // always stop this close, then decide once the key event has settled.
        // calling window.confirm() synchronously inside the Esc keydown lets
        // Safari's trailing Esc dismiss the native confirm the instant it
        // opens (it flashes and vanishes), so defer it to a macrotask that
        // runs after the keyup. the guard stops a repeated Esc from queuing
        // several confirms.
        evt.preventDefault()
        if (this._addEntryConfirming) return
        this._addEntryConfirming = true
        setTimeout(() => {
          this._addEntryConfirming = false
          if (window.confirm('Discard unsaved changes?')) {
            this.resetAddEntryForm()
            this.$bvModal.hide('add-entry-modal')
          }
        }, 0)
      }
    },

    onAddEntryHidden() {
      // flags only; the draft itself persists (see openAddEntry).
      this.addEntryError = ''
      this.addEntrySubmitting = false
    },

    addAnother() {
      this.addEntrySuccess = null
      this.resetAddEntryForm()
    },

    openCreatedEvent() {
      const eid = this.addEntrySuccess && this.addEntrySuccess.event_id
      this.$bvModal.hide('add-entry-modal')
      if (!eid) return
      this.$router.push({
        name: 'ops_desk',
        query: { ...this.$route.query, tenant: this.tenant, event_id: eid },
      }).catch(() => {})
    },

    resetAddEntryForm() {
      this.addEntry = {
        entry_type: 'incident',
        category: '',
        what_happened: '',
        driver_id: '',
        vehicle_id: '',
        charter_ref: '',
        location_text: '',
        extras: {},
      }
      this.addEntryPhotoKeys = []
      if (this.$refs.addEntryPhotoInput) this.$refs.addEntryPhotoInput.clear()
    },

    // FI-1812 photo staging: shared by the add-entry modal and the
    // detail-page gallery; both are authed surfaces on this tenant.
    async presignPhoto(file) {
      const res = await presign_ops_desk_attachment(this.tenant, {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
      })
      return (res && res.value && res.value.data) || null
    },

    cancelPhotoAdd() {
      this.showPhotoAdd = false
      this.detailPhotoKeys = []
      this.photoAddError = ''
      this.photoRemoveError = ''
      if (this.$refs.detailPhotoInput) this.$refs.detailPhotoInput.clear()
    },

    async attachPhotos() {
      if (!this.detailPhotoKeys.length || this.attachingPhotos) return
      this.attachingPhotos = true
      this.photoAddError = ''
      try {
        const res = await add_ops_desk_event_attachments(this.tenant, this.event_id, this.detailPhotoKeys)
        const data = (res && res.value && res.value.data) || {}
        this.attachments = this.attachments.concat(data.attachments || [])
        this.cancelPhotoAdd()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk attachPhotos failed', err)
        const body = err && err.response && err.response.data
        this.photoAddError = (body && body.message) || 'Could not attach the photos. Try again.'
      } finally {
        this.attachingPhotos = false
      }
    },

    async removePhoto(a) {
      if (this.removingAttachmentId) return
      const ok = await this.$bvModal.msgBoxConfirm(
        'Remove "' + (a.name || 'this photo') + '" from the event? This deletes it permanently.',
        { title: 'Remove photo', okTitle: 'Remove', okVariant: 'danger', cancelTitle: 'Keep', centered: true },
      ).catch(() => false)
      if (!ok) return
      this.removingAttachmentId = a.attachment_id
      this.photoRemoveError = ''
      try {
        await delete_ops_desk_event_attachment(this.tenant, this.event_id, a.attachment_id)
        this.attachments = this.attachments.filter(x => x.attachment_id !== a.attachment_id)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk removePhoto failed', err)
        const body = err && err.response && err.response.data
        this.photoRemoveError = (body && body.message) || 'Could not remove the photo. Try again.'
      } finally {
        this.removingAttachmentId = ''
      }
    },

    setAddEntryType(t) {
      if (this.addEntry.entry_type === t) return
      this.addEntry.entry_type = t
      this.addEntry.category = ''
      // wipe extras because schema membership changes by (type, category);
      // a stale value would survive into a different field's slot.
      this.addEntry.extras = {}
    },

    selectOptions(field) {
      const opts = Array.isArray(field.options) ? field.options : []
      return [
        { value: '', text: field.placeholder || 'Choose...', disabled: !!field.required },
        ...opts.map(o => ({ value: o.value, text: o.label || o.value })),
      ]
    },

    async submitAddEntry() {
      if (!this.canSubmitAddEntry || this.addEntrySubmitting) return
      this.addEntryError = ''
      this.addEntrySubmitting = true
      try {
        const e = this.addEntry
        const payload = {
          entry_type: e.entry_type,
          category: e.category || (e.entry_type === 'note' ? 'note' : ''),
          what_happened: (e.what_happened || '').trim(),
          driver_id: (e.driver_id || '').trim() || undefined,
          vehicle_id: (e.vehicle_id || '').trim() || undefined,
          charter_ref: (e.charter_ref || '').trim() || undefined,
          location_text: (e.location_text || '').trim() || undefined,
          // server splits schema-driven extras from first-class columns
          // (kudos_target, delay_duration_min); we just merge them in flat.
          ...e.extras,
          // after the spread so a schema field can never shadow it
          attachment_keys: this.addEntryPhotoKeys.length ? this.addEntryPhotoKeys : undefined,
        }
        const res = await create_ops_desk_event(this.tenant, payload)
        const data = (res && res.value && res.value.data) || {}
        this.addEntrySuccess = data
        // the draft is consumed once the entry lands
        this.resetAddEntryForm()
        // refresh the list so the freshly added entry shows up without
        // requiring a manual reload.
        this.load_data()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk submitAddEntry failed', err)
        // api errors are a flat {message} body; keep the error_data
        // path as a fallback for wrapped shapes.
        const body = err && err.response && err.response.data
        const msg = body && (body.message
          || (body.error_data && (body.error_data.error_message || body.error_data.message)))
        this.addEntryError = msg || 'Could not save the entry.'
      } finally {
        this.addEntrySubmitting = false
      }
    },

  },
}
</script>

<style scoped>
/* full-width page, matching the support-ticket list: the dense table wants
   the whole viewport so the driver / what-happened columns stop wrapping and
   the left+right gutters (the old 1560 cap centered on wide monitors)
   disappear. the detail view re-caps itself below to stay a readable column. */
.od-root {
  max-width: none;
  margin: 0;
  padding: 12px 20px 32px 20px;
  text-align: left;
  font-size: 14px;
  color: #1d1d1f;
}
/* detail stays a readable column, flush-left with the list's gutter. */
.od-detail { max-width: 1560px; }
@media (max-width: 720px) {
  .od-root { padding: 12px 16px 24px 16px; }
}

/* shared header rail. matches ops_desk_settings.vue (steelblue accent,
   eyebrow + title stacked, 32px circular back). list mode omits the
   back button; detail mode includes it. actions float to the right. */
.od-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  /* square rail edge, soft right side -- the support-ticket rail treatment
     (matches .od-content-rail below) */
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}
.od-rail-back {
  background: #fff;
  border: 1px solid #c8d4df;
  width: 32px; height: 32px;
  border-radius: 50%;
  color: var(--fi-rail-accent);
  cursor: pointer;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.od-rail-back:hover { background: var(--fi-rail-accent); color: #fff; }
.od-rail-text {
  display: flex; flex-direction: column; justify-content: center;
  min-height: 32px;
  min-width: 0;
  flex: 1 1 auto;
}
.od-rail-eyebrow {
  font-size: 11px; line-height: 1.4;
  text-transform: uppercase; letter-spacing: 0.6px;
  text-indent: -0.5px;
  color: var(--fi-ops-eyebrow); font-weight: 600;
  margin-bottom: 1px;
}
.od-rail-title {
  margin: 0; padding: 0;
  font-size: 19px; line-height: 1.2;
  font-weight: 700; color: #1d1d1f;
  letter-spacing: -0.1px;
  display: flex; align-items: center; gap: 8px;
}
.od-rail-title--muted { color: #6b7280; }
.od-rail-sub { font-size: 12px; color: #6b7280; margin-top: 1px; }
.od-rail-actions {
  display: inline-flex; gap: 6px;
  flex-shrink: 0;
}
/* admin tenant-context chip: names whose data the page renders when the
   route tenant differs from the navbar selection. sits inside the flex
   title so the existing 8px gap spaces it off the baseline. */
.od-rail-tenant-chip {
  display: inline-flex; align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #c8d4df;
  color: var(--fi-ops-eyebrow);
  font-size: 11px; font-weight: 600;
  letter-spacing: 0;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .od-rail { padding: 10px 12px; gap: 10px; }
  .od-rail-title { font-size: 17px; }
  .od-rail-actions { flex-wrap: wrap; gap: 4px; }
}

/* day-chip pill style, lifted from the huddle settings tab so the
   visual language stays consistent across the surface. */
.od-chip-filters {
  display: flex; flex-wrap: wrap; gap: 14px;
  margin-bottom: 12px;
}
.od-chip-group {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.od-chip-group-label {
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .4px;
  color: #6b7280; margin-right: 2px; align-self: center;
}
.od-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 4px 12px; font-size: 12.5px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
/* neutral chips (the "All" reset) keep the plain wash on hover; colored
   chips below own their own hover so the steelblue wash can't strip the hue */
.od-chip:not([class*="od-fc-"]):hover { background: #eef4fb; border-color: #b8c8da; }
.od-chip--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.od-chip:not([class*="od-fc-"]).od-chip--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

/* status/type filter pills echo the row chip palette, mirroring the vsm
   board's filter chips: a clear colored outline when idle, a solid fill of
   the same hue when active. the neutral "All" pill keeps the base .od-chip
   look so it reads as the reset. */
.od-fc-gray  { background: #fff; border-color: #6b7280; color: #4b5563; }
.od-fc-blue  { background: #fff; border-color: #1668d6; color: #11468f; }
.od-fc-amber { background: #fff; border-color: #d39410; color: #7e5407; }
.od-fc-green { background: #fff; border-color: #1fab42; color: #146230; }
.od-fc-red   { background: #fff; border-color: #d62f2f; color: #b22525; }
.od-chip[class*="od-fc-"] { border-width: 1.5px; }
/* idle outline pills take a neutral wash on hover (brightness would wash the
   white fill to near-invisible); scoped to idle so active solids keep their fill */
.od-chip[class*="od-fc-"]:not(.od-chip--active):hover { background: #f4f7fa; }
.od-fc-gray.od-chip--active  { background: #5f6772; border-color: #5f6772; color: #fff; }
.od-fc-blue.od-chip--active  { background: #1668d6; border-color: #1668d6; color: #fff; }
.od-fc-amber.od-chip--active { background: #d39410; border-color: #d39410; color: #15202b; }
.od-fc-green.od-chip--active { background: #1fab42; border-color: #1fab42; color: #15202b; }
.od-fc-red.od-chip--active   { background: #d62f2f; border-color: #d62f2f; color: #fff; }

/* family focus standard for the custom (non-bootstrap) controls */
.od-chip:focus-visible,
.od-id-link:focus-visible,
.od-clear-btn:focus-visible,
.od-add-type-btn:focus-visible,
.od-rail-back:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}

/* FI-1815 "+ Add entry" modal. compact single-column form with three big
   entry-type buttons up top, then category + conditional fields, then
   always-on context fields. tight spacing so the modal stays under-fold
   on a 13" laptop with the most common configurations. */
.od-add-loading { padding: 24px; text-align: center; color: #6b7280; font-size: 13.5px; }
.od-add-type-row {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;
  margin-bottom: 14px;
}
.od-add-type-btn {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 6px;
  padding: 10px 8px; font-size: 13px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .15s, border-color .15s, color .15s;
}
.od-add-type-btn:hover { background: #eef4fb; border-color: #b8c8da; }
.od-add-type-btn--active {
  background: var(--fi-ops-accent); border-color: var(--fi-ops-accent); color: #fff;
}
.od-add-type-btn--active:hover { background: var(--fi-ops-accent-strong); border-color: var(--fi-ops-accent-strong); }

.od-add-field { margin-bottom: 12px; }
.od-add-field label {
  display: block; margin-bottom: 4px;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.od-add-help { font-size: 12px; color: #6b7280; margin: -2px 0 4px 0; }
.od-add-req { color: #b91c1c; font-weight: 700; }

/* tinted panel for the category-driven fields; mirrors the public
   form's .op-public-section--conditional treatment */
.od-add-conditional {
  background: #f8fafc; border: 1px solid #e1e8f0;
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 6px; padding: 12px 14px 2px 14px;
  margin-bottom: 12px;
}

.od-add-row {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px;
  margin-bottom: 12px;
}
.od-add-col label {
  display: block; margin-bottom: 4px;
  font-size: 12px; font-weight: 700; color: #4b5563;
  text-transform: uppercase; letter-spacing: 0.04em;
}

.od-add-error {
  background: #fef2f2; border: 1px solid #fecaca;
  color: #b91c1c; border-radius: 4px;
  padding: 8px 10px; font-size: 13px; margin: 8px 0 12px 0;
}
.od-add-actions {
  display: flex; gap: 8px; justify-content: flex-end;
  margin-top: 14px; padding-top: 12px; border-top: 1px solid #eef0f3;
}
/* the draft-clearing link sits apart from the close/submit pair */
.od-add-discard { margin-right: auto; padding-left: 0; }

.od-add-success { text-align: center; padding: 12px 8px 4px 8px; }
.od-add-success .fa-check-circle { font-size: 38px; color: #16a34a; margin-bottom: 8px; }
.od-add-success-headline { font-size: 16px; font-weight: 600; color: #1d1d1f; margin-bottom: 4px; }
.od-add-success-body { font-size: 13px; color: #4b5563; margin-bottom: 14px; }
.od-add-success .od-add-actions { justify-content: center; border-top: none; padding-top: 4px; }

@media (max-width: 600px) {
  .od-add-type-row { grid-template-columns: 1fr; }
  .od-add-row { grid-template-columns: 1fr; }
}

.od-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef0f3;
  margin-bottom: 10px;
  font-size: 13px;
}
.od-filter-field { display: inline-flex; align-items: center; gap: 4px; }
.od-filter-input { width: 150px; }
.od-filter-select { width: 160px; }

/* search pill, ported from the vsm board: rounded field anchored to the
   right of the filter row, leading magnifier, ✕ clear when populated. */
.od-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 240px;
}
.od-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.od-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid #d6dde6; border-radius: 999px;
  background: #fff; color: #1d1d1f;
  transition: border-color .12s, box-shadow .12s;
}
.od-search-input::placeholder { color: #9aa3ad; }
.od-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.od-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
/* invisible hit-area extension to a comfortable tap target */
.od-search-clear::before {
  content: '';
  position: absolute;
  top: -9px; bottom: -9px; left: -7px; right: -7px;
}
.od-search-clear:hover { color: #4b5563; }
.od-search-input:focus-visible,
.od-search-clear:focus-visible {
  outline: 2px solid var(--fi-ops-accent);
  outline-offset: 2px;
}
@media (max-width: 600px) {
  /* full-width search leading the filter row; 16px to dodge iOS focus-zoom */
  .od-search { margin-left: 0; flex: 1 0 100%; order: -1; }
  .od-search-input { font-size: 16px; }
}

.od-table-wrap { width: 100%; }
/* keep bootstrap-vue's responsive wrapper as the horizontal scroll container
   and pin overflow-y to hidden (rather than letting it compute to auto from
   overflow-x:auto) so classic scrollbars in Safari/Firefox can't spawn a
   phantom vertical scrollbar over a wide list. mirrors the vsm board. */
.od-table-inline >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }
.od-table-inline >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.od-table-inline >>> .table.b-table th,
.od-table-inline >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.od-table-inline >>> .table.b-table th:first-child,
.od-table-inline >>> .table.b-table td:first-child { padding-left: 12px !important; }
.od-table-inline >>> .table.b-table th:last-child,
.od-table-inline >>> .table.b-table td:last-child   { padding-right: 12px !important; }
.od-table-inline >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  position: sticky; top: 0; z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.od-table-inline >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; cursor: pointer; }

.od-table-inline >>> th[aria-sort] { background-image: none !important; padding-right: 0.75rem !important; white-space: nowrap; }
.od-table-inline >>> th[aria-sort]::before { display: none !important; }
.od-table-inline >>> th[aria-sort]::after  { display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999; vertical-align: middle; float: none !important; width: auto !important; height: auto !important; opacity: 1 !important; }
.od-table-inline >>> th[aria-sort="none"]::after       { content: "\2195" !important; }
.od-table-inline >>> th[aria-sort="ascending"]::after  { content: "\25B2" !important; color: var(--fi-rail-accent); }
.od-table-inline >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

/* single-line snippet. max-width: 0 on the td lets the flexible column
   absorb leftover width without growing to content, so the ellipsis can
   engage; the span clips inside it. */
.od-table-inline >>> td.od-snippet-cell { max-width: 0; }
/* driver column sizes to its content (the longest "Last, First [id]") on one
   line instead of wrapping in a fixed width; width:1% + nowrap is the
   shrink-to-fit idiom, and the what-happened column absorbs the freed space.
   theme-agnostic (structural). */
.od-table-inline >>> .od-driver-cell { white-space: nowrap; width: 1%; }
.od-snippet {
  display: block;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* id cell: a real focusable control so keyboard users can open events */
.od-id-link {
  background: none; border: none; padding: 0;
  font-size: 13px; color: var(--fi-ops-accent-strong); text-decoration: underline;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.od-id-link:hover { color: var(--fi-ops-accent-deep); }

/* list-cell timestamp: monospaced numerals, single-line so adjacent
   cells (driver / vehicle / status) stay vertically aligned. */
.od-list-dt {
  display: inline-block;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: #374151;
}

/* type chip — three small pastel pills with a leading glyph. icon-driven
   so the column reads as a sortable type indicator rather than a label.
   icon-only-on-mobile drop happens by gating .od-type-chip-text below
   the snippet col. */
.od-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.4;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 999px;
  border: 1px solid;
  white-space: nowrap;
}
.od-type-chip .fa { font-size: 10px; line-height: 1; }
.od-type-chip--incident { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.od-type-chip--kudos    { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.od-type-chip--note     { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }

/* category chip — neutral steelblue pill. lighter weight than type/status
   so the row doesn't read as three competing chips. */
.od-cat-chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11.5px;
  line-height: 1.4;
  font-weight: 600;
  border-radius: 4px;
  background: #eef4fb;
  color: #1f5582;
  border: 1px solid #d6e1ee;
  white-space: nowrap;
}

/* detail header now shares .od-rail with the list view (see above). */

.od-content-rail {
  background: #f4f8ff;
  box-shadow: inset 4px 0 0 0 var(--fi-rail-accent);
  padding: 14px 16px 14px 18px;
  border-radius: 0 6px 6px 0;
  margin-top: 6px;
}
.od-subject-banner {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.35;
  white-space: pre-wrap;
}
.od-subject-edit-note { font-size: 12px; color: #6b7280; margin-top: 6px; }

.od-status-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
}
.od-status-actions { display: inline-flex; gap: 6px; flex-wrap: wrap; }
.od-status-move-label {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  color: #6b7280; margin-right: 2px; align-self: center;
}

/* family chip geometry + the spec trios, matching the type chips so a
   row carries one chip system. */
.od-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1.4;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border: 1px solid;
  white-space: nowrap;
}
.od-status-chip .fa { font-size: 10px; line-height: 1; }
.od-status-chip--new                { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.od-status-chip--in_progress        { background: #e7f1ff; color: #0b3d91; border-color: #bcd8fb; }
.od-status-chip--awaiting_followup  { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.od-status-chip--resolved           { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }

/* inline mutation-failure feedback (toast carries the same message) */
.od-detail-error {
  margin-top: 10px;
  background: #fbe3e5; border: 1px solid #f5c0c4; color: #b91c1c;
  border-radius: 4px; padding: 8px 10px; font-size: 13px;
}
.od-comment-error { margin-top: 6px; font-size: 12.5px; color: #b91c1c; }

.od-table {
  width: 100%;
  margin-top: 18px;
  border-collapse: collapse;
  table-layout: fixed;
}
.od-cell {
  padding: 8px 12px 8px 0;
  vertical-align: top;
  border-bottom: 1px solid #eef0f3;
}
.od-cell h5,
.od-admin-grid h5 {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6b7280;
  margin: 0 0 3px 0;
}
.od-value { font-size: 14px; color: #1d1d1f; }
.od-edit-label {
  display: block; font-size: 11px; color: #6b7280; text-transform: uppercase;
  letter-spacing: 0.3px; margin-bottom: 4px;
}

/* per-tr 2-col mobile grid */
@media (max-width: 720px) {
  .od-table, .od-table > tbody, .od-table > tbody > tr { display: block; }
  .od-table > tbody > tr {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 14px; row-gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #eef0f3;
  }
  .od-table > tbody > tr > .od-cell { border-bottom: none; padding: 0; }
  .od-table > tbody > tr > .od-cell[colspan="4"] { grid-column: 1 / -1; }
}
@media (max-width: 400px) {
  .od-table > tbody > tr { grid-template-columns: 1fr; }
}

.od-section-card { margin-top: 28px; }
.od-section-header { padding-bottom: 6px; border-bottom: 1px solid #e0e0e0; margin-bottom: 12px; }
/* ops accent, not the rail accent: steelblue is 4.1:1 on white and fails
   aa at this size; the darkened steel passes in both themes */
.od-section-header h4 { margin: 0; font-size: 14px; color: var(--fi-ops-accent); font-weight: 600; }
.od-section-body { font-size: 14px; }
.od-pre { white-space: pre-wrap; word-break: break-word; }
.od-empty-inline { color: #6b7280; font-style: italic; }
.od-empty-inline .od-empty-add { padding: 0 6px; font-size: 12.5px; font-style: normal; }

.od-section-card--admin {
  background: var(--fi-admin-cue-bg);
  border-left: 3px solid var(--fi-admin-cue-line);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-top: 24px;
}
.od-section-header--admin { border-bottom: none; margin-bottom: 8px; padding-bottom: 0; }
.od-section-header--admin h4 { color: #a26717; }
.od-admin-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
}
@media (max-width: 720px) {
  .od-admin-grid { grid-template-columns: 1fr; }
}

/* FI-1813 submission-details extras grid */
.od-extras-grid {
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 6px 14px;
  font-size: 13px;
}
/* the row div exists for the v-for key; contents lets each label/value
   pair place into the grid columns instead of occupying one cell */
.od-extra-row { display: contents; }
.od-extra-label {
  color: #6b7280; font-weight: 600;
  text-align: right; padding-top: 1px;
}
.od-extra-value {
  color: #1d1d1f;
  word-break: break-word;
}
@media (max-width: 540px) {
  .od-extras-grid { grid-template-columns: 1fr; }
  .od-extra-label { text-align: left; }
}

/* comments */
.od-comment {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}
.od-comment:last-of-type { border-bottom: none; }
.od-comment-avatar {
  flex: 0 0 40px;
  width: 40px; height: 40px;
  border-radius: 50%;
  background: #eef4fb;
  border: 1px solid #b8d0e4;
  color: var(--fi-rail-accent);
  font-size: 15px; font-weight: 600;
  display: inline-flex; align-items: center; justify-content: center;
}
.od-comment-body { flex: 1; min-width: 0; }
.od-comment-head { display: flex; align-items: baseline; gap: 8px; }
.od-comment-name { font-size: 15px; font-weight: 600; }
.od-comment-time { font-size: 12.5px; color: #6b7280; margin-top: 3px; cursor: help; }
.od-comment-via {
  font-size: 11px;
  color: #6b7280;
  background: #f3f6fa;
  border: 1px solid #e1e8f0;
  border-radius: 999px;
  padding: 1px 8px;
  margin-left: 8px;
  white-space: nowrap;
}

/* FI-1812 photo gallery on the detail view */
.od-photo-count {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f6fa;
  border-radius: 999px;
  padding: 1px 8px;
  margin-left: 8px;
  vertical-align: 2px;
}
.od-photo-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 6px;
}
.od-photo-thumb {
  display: block;
  width: 108px;
  height: 108px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #d6dde6;
  background: #f8fafc;
}
.od-photo-thumb:hover { border-color: var(--fi-rail-accent); }
.od-photo-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.od-photo-tile { position: relative; }
.od-photo-remove {
  position: absolute; top: -7px; right: -7px;
  width: 22px; height: 22px; border-radius: 50%;
  border: 1px solid #d6dde6; background: #fff; color: #6b7c90;
  font-size: 11px; line-height: 1; cursor: pointer; padding: 0;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(20, 33, 46, 0.18);
}
.od-photo-remove:hover { color: #c0392b; border-color: #c0392b; }
.od-photo-remove:disabled { cursor: default; opacity: 0.6; }
.od-sos-body { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.od-sos-text { font-size: 13px; color: #5b6b7c; }
.od-add-sos {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
  margin: 0 0 12px; padding: 9px 12px;
  border: 1px solid #f0d9a8; border-radius: 6px; background: #fdf6e7;
  font-size: 12.5px; color: #6b5518;
}
.od-add-sos-text strong { color: #8a6d1a; }
.od-add-sos-btn {
  white-space: nowrap; padding: 5px 10px; border-radius: 5px;
  background: #b7791f; color: #fff !important;
  font-size: 12px; font-weight: 600; text-decoration: none !important;
}
.od-add-sos-btn:hover { background: #9c6410; }
.od-photo-add-panel {
  margin-top: 10px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e1e8f0;
  border-radius: 6px;
}
.od-comment-text { margin-top: 3px; white-space: pre-wrap; word-break: break-word; }

.od-comment-composer { margin-top: 16px; }
.od-composer-actions { display: flex; justify-content: flex-end; margin-top: 8px; }

/* empty state */
.od-empty-card {
  margin: 36px auto;
  max-width: 480px;
  text-align: center;
  color: #6b7280;
  padding: 32px 16px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
}
.od-empty-card .fa { font-size: 28px; color: #9ca3af; }
.od-empty-headline { font-size: 16px; color: #374151; margin: 10px 0 4px 0; font-weight: 600; }
.od-empty-body { font-size: 13.5px; margin-bottom: 14px; }
.od-first-run-actions { display: flex; gap: 8px; justify-content: center; margin-top: 10px; }

/* filtered-to-zero line inside the b-table empty slot */
.od-table-empty { font-size: 13px; color: #6b7280; padding: 8px; text-align: center; }
.od-clear-btn {
  background: none; border: none; padding: 0; margin-left: 2px;
  font-size: 13px; color: var(--fi-ops-accent-strong); text-decoration: underline;
  cursor: pointer;
}
.od-clear-btn:hover { color: var(--fi-ops-accent-deep); }

/* load-failure card: advisory amber + retry, shared by list and detail */
.od-load-error {
  display: flex; align-items: center; gap: 10px;
  background: #fff8ef; border: 1px solid #f0d9b5; color: #7a5400;
  border-radius: 6px; padding: 10px 14px; font-size: 13.5px;
  margin: 12px 0;
}

/* table-busy shimmer rows (same skeleton treatment as the detail view) */
.od-busy-rows { padding: 4px 0; }
.od-busy-row { height: 14px; margin: 8px 0; }
.od-busy-row:nth-child(2) { width: 96%; }
.od-busy-row:nth-child(3) { width: 91%; }
.od-busy-row:nth-child(4) { width: 97%; }
.od-busy-row:nth-child(5) { width: 88%; }

/* infinite scroll footer: a thin loading row and a zero-height sentinel the
   observer geometry watches. the sentinel keeps a little height so it is a
   real layout box the scroll math can measure. */
.od-loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 0 6px;
  color: #6b7280;
  font-size: 12px;
}
.od-loading-more-spinner {
  width: 14px; height: 14px;
  border: 2px solid #d1d5db;
  border-top-color: var(--fi-rail-accent, #2f6f8f);
  border-radius: 50%;
  animation: od-spin 0.7s linear infinite;
}
@keyframes od-spin { to { transform: rotate(360deg); } }
.od-scroll-sentinel { height: 1px; }

/* ---- mobile card list (≤768px, rendered via isMobileView) ---------------
   each card is a full-width tap target that opens the event detail, mirroring
   the support-ticket / booking-customer mobile lists. reuses the desktop
   type/status/category chips so the two views read identically. */
.od-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 2px 4px;
}
.od-mobile-card {
  display: block;
  width: 100%;
  text-align: left;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #e0e0e0);
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}
.od-mobile-card:hover,
.od-mobile-card:focus-visible {
  background: var(--fi-brand-tint, #e9f3fc);
  border-color: var(--fi-brand-tint-border, #c2ddf4);
  outline: none;
}
.od-mobile-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.od-mobile-card-id {
  font-size: 12px;
  font-weight: 700;
  color: var(--fi-muted, #6b7280);
  font-variant-numeric: tabular-nums;
}
.od-mobile-card-chips {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.od-mobile-card-subject {
  font-size: 14px;
  font-weight: 600;
  color: var(--fi-text, #333);
  line-height: 1.35;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}
.od-mobile-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 12px;
  color: var(--fi-muted, #555);
}
.od-mobile-meta-icon { margin-right: 4px; color: #9ca3af; }
.od-mobile-card-dt {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.od-mobile-empty {
  color: var(--fi-muted, #888);
  font-size: 13px;
  text-align: center;
  padding: 20px 10px;
}
/* skeleton cards while the first fetch runs (shape mirrors the real card) */
.od-mobile-card--skeleton { pointer-events: none; cursor: default; }
.od-mobile-card--skeleton:hover { background: var(--fi-surface, #fff); border-color: var(--fi-line, #e0e0e0); }
.od-skel {
  display: inline-block;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: od-skel-shimmer 1.4s ease-in-out infinite;
}
@keyframes od-skel-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.od-skel--id      { width: 34px; height: 12px; }
.od-skel--chip    { width: 130px; height: 20px; border-radius: 4px; }
.od-skel--subject { display: block; width: 90%; height: 14px; margin: 8px 0 0; }
.od-skel--subject-2 { width: 60%; margin-top: 4px; }
.od-skel-meta-row { display: flex; gap: 10px; margin-top: 10px; }
.od-skel--meta { width: 70px; height: 11px; }

/* skeleton */
.od-skeleton { padding: 12px 0; }
.skeleton {
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: od-shimmer 1.4s ease-in-out infinite;
  border-radius: 4px;
}
.skeleton-line { display: block; }
@keyframes od-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>

<!-- unscoped: bootstrap-vue renders the modal at the <body> root, outside
     this component's scoped DOM, so these dialog rules can't be scoped. -->
<style>
/* ERM settings modal: roomy on desktop, full-screen on phones so the tabbed
   form is comfortable to fill on a small viewport. */
.od-settings-modal .modal-body { padding: 16px 18px 22px; }
/* tenant chip in the modal title. the modal renders at the <body> root, so
   ops_desk.vue's scoped chip style can't reach it -- restated here with
   tokens so it reads correctly in every theme (light fallbacks for
   Classic/Horizon; the modal is a Midnight scope root, so tokens go dark). */
.od-settings-modal .od-rail-tenant-chip {
  display: inline-flex; align-items: center;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #c8d4df);
  color: var(--fi-text-muted, #5b6b7a);
  font-size: 11px; font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}
@media (max-width: 768px) {
  .od-settings-modal .modal-dialog {
    max-width: 100%;
    width: 100%;
    margin: 0;
    min-height: 100%;
  }
  .od-settings-modal .modal-content {
    min-height: 100vh;
    border: 0;
    border-radius: 0;
  }
  .od-settings-modal .modal-body { padding: 12px 14px 20px; }
}

/* FI-1932: printable event detail. body.ops-desk-print is set while this page
   is mounted (see mounted/beforeDestroy), mirroring the trip-sheet / vsm-board
   print paths, so a print from anywhere else in the app is untouched. hides the
   app chrome and every screen-only control, leaving a clean single column, then
   keeps the banner / chip / coaching fills on paper. */
@media print {
  /* app chrome: classic navbar + fa launcher, and the horizon top / section
     nav. reset the horizon content offset so the page starts at the edge. */
  body.ops-desk-print #navbar,
  body.ops-desk-print .fa-launcher-root,
  body.ops-desk-print .fi-tn,
  body.ops-desk-print .fi-sn,
  body.ops-desk-print .fi-sn-scrim { display: none !important; }
  body.ops-desk-print .fi-hz-content { margin-left: 0 !important; padding: 0 !important; }

  /* screen-only controls inside the event: the rail buttons + back arrow, the
     status transition buttons (the chip itself stays), the comment composer,
     the photo add panel + per-tile remove, and every inline "+ Add". */
  body.ops-desk-print .od-no-print,
  body.ops-desk-print .od-status-actions,
  body.ops-desk-print .od-comment-composer,
  body.ops-desk-print .od-photo-add-panel,
  body.ops-desk-print .od-photo-remove,
  body.ops-desk-print .od-empty-add { display: none !important; }

  /* single clean column: drop the card shadows and keep content from being
     clipped, and try not to split a section across a page break. */
  body.ops-desk-print .od-detail { padding: 0 !important; }
  body.ops-desk-print .od-section-card {
    box-shadow: none !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* keep the subject banner, status chip, and admin coaching-rail fills on
     paper rather than dropping to white. */
  body.ops-desk-print .od-detail,
  body.ops-desk-print .od-content-rail,
  body.ops-desk-print .od-status-chip,
  body.ops-desk-print .od-section-card--admin {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
